// Guide Orb — node definitions
// 5 zones spread evenly across 360° of longitude (~72° per slice)
//
// Zone centres (lon):
//   TRADING    —  -144°  (slice -180 to -108)
//   LEARN      —   -72°  (slice -108 to  -36)
//   F&O        —     0°  (slice  -36 to  +36)
//   ANALYSIS   —   +72°  (slice  +36 to +108)
//   DISCIPLINE —  +144°  (slice +108 to +180)

export const GUIDE_ZONES = [
  { id: 'trading',    label: 'Trading',    color: '#16C784', lon: -144, lat: 72 },
  { id: 'learn',      label: 'Learn',      color: '#f97316', lon:  -72, lat: 72 },
  { id: 'fo',         label: 'F&O',        color: '#f59e0b', lon:    0, lat: 72 },
  { id: 'analysis',   label: 'Analysis',   color: '#06b6d4', lon:   72, lat: 72 },
  { id: 'discipline', label: 'Discipline', color: '#8b5cf6', lon:  144, lat: 72 },
];

export const GUIDE_NODES = [

  // ── TRADING zone ──────────────────────────────────────────────────────────────

  {
    id: 'market-order',
    label: 'Market\nOrder',
    color: '#16C784',
    zone: 'trading',
    position: { lat:  48, lon: -162 },
    steps: [
      {
        title: 'Step 1 — Pre-market check',
        body: 'Before placing any trade, open Discover → Global Cues. Check SGX Nifty direction, overnight US market close, and FII/DII flows. This tells you whether today favours longs or shorts.',
        icon: 'Globe',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['SGX +0.4%', 'US Green', 'FII Buying', 'DII Buying', 'Crude Stable'],
        },
      },
      {
        title: 'Step 2 — Find a setup via Scanner',
        body: 'Go to Discover → Scanners. Look for a stock in the Patterns or Signals tab with high confluence. Tap the card to open the stock detail and confirm the chart story makes sense.',
        icon: 'ScanLine',
        ui: {
          type: 'search',
          stock: 'RELIANCE',
          price: '₹1,362.96',
          change: '+0.84% · Breakout signal',
        },
      },
      {
        title: 'Step 3 — Open the order ticket',
        body: 'Go to Trade (bottom nav). Search the stock symbol or find it in your watchlist. Tap the green B (Buy) or red S (Sell) button to open the order ticket.',
        icon: 'TrendingUp',
        ui: {
          type: 'search',
          stock: 'RELIANCE',
          price: '₹1,362.96',
          change: '+0.84%',
        },
      },
      {
        title: 'Step 4 — Choose Intraday or Swing',
        body: 'At the top of the ticket select your product type:\n• Intraday — position must close same day, executes at market price\n• Swing/CNC — hold overnight, no forced exit\nFor a Market order, choose Intraday here.',
        icon: 'Zap',
        ui: {
          type: 'producttype',
          active: 'INTRADAY',
        },
      },
      {
        title: 'Step 5 — Set quantity',
        body: 'Use + / − to set shares. The MARKET / LIMIT toggle sits next to the quantity counter — MARKET executes immediately, LIMIT waits for your price. Watch the capital summary below: Price × Qty and total capital used update live as you adjust.',
        icon: 'Hash',
        ui: {
          type: 'quantity',
          qty: 10,
          capital: '₹13,630',
          limitPrice: 'MARKET order · fills at live price',
        },
      },
      {
        title: 'Step 6 — Set Stop Loss (required for Intraday)',
        body: 'Enter a Stop Loss price in the SL field. For a BUY trade, place SL just below the nearest support. The risk panel instantly shows your max loss in ₹. If it\'s too large, cut quantity.',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '',
          slLabel: 'Stop Loss (₹) *Required',
          targetLabel: 'Target (₹)',
          highlight: 'sl',
        },
      },
      {
        title: 'Step 7 — Set Target',
        body: 'Enter a target price. The Risk:Reward ratio appears automatically. Aim for minimum 1:2 — if your SL risk is ₹500, your target should give ₹1,000+.',
        icon: 'Target',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '1,386.00',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.1',
          highlight: 'target',
        },
      },
      {
        title: 'Step 8 — Why this trade? (Required)',
        body: 'Scroll to the "Why this Trade?" section. Tap one reason — Strategy, Scanner, Technical, Fundamental, Sentiment. This is mandatory and feeds your Discipline Score. Never pick Impulse.',
        icon: 'Brain',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['Strategy','Scanner','Technical','Fundamental','Sentiment','Tip','Impulse'],
        },
      },
      {
        title: 'Step 9 — Technical tags & Confidence',
        body: 'Optionally tag the setup (Breakout, RSI, Support, etc.). Then set your Confidence slider 1–10. Be honest — overconfident traders consistently underperform. 6–7 is healthy for most setups.',
        icon: 'Activity',
        ui: {
          type: 'tagsconfidence',
          activeTags: ['Support','Volume Spike'],
          tags: ['Breakout','RSI','MACD','Volume Spike','Candlestick','Support','Resistance','Trendline'],
          confidence: 7,
        },
      },
      {
        title: 'Step 10 — Review AI quality score',
        body: 'If an AI quality score appears, read it. Score ≥7 + "Proceed" = your setup is solid. Score ≤4 = reconsider. It checks your SL placement, R:R, and trade reason against your history.',
        icon: 'Award',
        ui: {
          type: 'placebutton',
          label: 'BUY 10 Shares — MARKET',
          color: '#16C784',
          status: 'AI score: 7.4 / 10 — SL logical, R:R 2.1 ✓, reason: Technical',
        },
      },
      {
        title: 'Step 11 — Place the order',
        body: 'Swipe the green button to execute. Market orders fill immediately at the current sim-date price. Check the Orders tab to confirm it shows as Open with your SL and target attached.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY 10 Shares — MARKET',
          color: '#16C784',
          status: 'Filled — position open at ₹1,362.96',
        },
        cta: { label: 'Go to Trade →', path: '/trade' },
      },
    ],
  },

  {
    id: 'limit-order',
    label: 'Limit\nOrder',
    color: '#16C784',
    zone: 'trading',
    position: { lat:  20, lon: -150 },
    steps: [
      {
        title: 'Go to Trade → Search stock',
        body: 'Tap the Trade tab (bottom nav). Type "RELIANCE" in the search bar. Tap the stock row to open the order ticket. Current price shows at the top — ₹1,362.96.',
        icon: 'Search',
        ui: {
          type: 'search',
          stock: 'RELIANCE',
          price: '₹1,362.96',
          change: '+1.04%',
        },
      },
      {
        title: 'Select BUY or SELL',
        body: 'At the top of the ticket tap BUY (green) for a long position or SELL (red) to short. For this example we\'re buying RELIANCE at a pullback — tap BUY.',
        icon: 'TrendingUp',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'RELIANCE',
          price: '₹1,362.96',
          change: '+1.04%',
        },
      },
      {
        title: 'Choose Intraday or Swing',
        body: 'Tap "Swing / CNC" for a hold-overnight trade, or "Intraday" for a same-day exit. For a limit order waiting for a pullback, choose Swing — it stays open until filled.',
        icon: 'Calendar',
        ui: {
          type: 'producttype',
          active: 'SWING',
        },
      },
      {
        title: 'Switch to LIMIT mode',
        body: 'Below the product toggle, tap LIMIT. A "Limit Price (₹)" field appears. RELIANCE is at ₹1,362 but you want to buy at the support level ₹1,340 — enter that here.',
        icon: 'Target',
        ui: {
          type: 'ordermode',
          active: 'LIMIT',
          limitPrice: '1,340.00',
        },
      },
      {
        title: 'Set Quantity',
        body: 'Tap + to increase shares or − to decrease. Watch the capital bar — it shows how much of your daily limit this uses. For ₹1,340 × 5 shares = ₹6,700 capital used.',
        icon: 'Hash',
        ui: {
          type: 'quantity',
          qty: 5,
          capital: '₹6,700',
          limitPrice: '1,340.00',
        },
      },
      {
        title: 'Enter Stop Loss',
        body: 'Type ₹1,318 in the Stop Loss field — just below the support level at ₹1,320. Risk = (1,340 − 1,318) × 5 = ₹110. The app shows "Recommended" in orange — that\'s the auto-suggested SL based on ATR.',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '1,318.00',
          target: '',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          highlight: 'sl',
        },
      },
      {
        title: 'Enter Target',
        body: 'Type ₹1,384 in the Target field — the next resistance zone. Risk = ₹110, Reward = (1,384 − 1,340) × 5 = ₹220. R:R = 1:2 ✓. The app shows this ratio automatically.',
        icon: 'TrendingUp',
        ui: {
          type: 'sltp',
          sl: '1,318.00',
          target: '1,384.00',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.0',
          highlight: 'target',
        },
      },
      {
        title: 'Why this Trade? (Required ✱)',
        body: 'Tap one reason — this is mandatory. For a level-based limit order tap "Technical". Tap any chip to see what it means and when it applies.',
        icon: 'Brain',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['Strategy','Scanner','Technical','Fundamental','Sentiment','Tip','Impulse'],
        },
      },
      {
        title: 'Technical Tags + Confidence',
        body: 'Tap matching tags — Support and Trendline for this setup. Set Confidence to 7/10 — you\'ve done your analysis, the level is clear. Don\'t set 10/10 unless you\'re certain, overconfidence hurts your score.',
        icon: 'Activity',
        ui: {
          type: 'tagsconfidence',
          activeTags: ['Support','Trendline'],
          tags: ['Breakout','RSI','MACD','Volume Spike','Candlestick','Support','Resistance','Trendline'],
          confidence: 7,
        },
      },
      {
        title: 'Tap "BUY 5 Shares" to place',
        body: 'Scroll down and tap the BUY button. The order goes Pending — it fills automatically when RELIANCE drops to ₹1,340. Check Orders tab to see it listed as "Pending Limit".',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY 5 Shares',
          color: '#16C784',
          status: 'Pending — fills when price hits ₹1,340',
        },
        cta: { label: 'Go to Trade →', path: '/trade' },
      },
    ],
  },

  {
    id: 'stop-loss',
    label: 'Stop Loss\n& Target',
    color: '#16C784',
    zone: 'trading',
    position: { lat: -10, lon: -162 },
    steps: [
      {
        title: 'What Stop Loss actually does',
        body: 'A Stop Loss (SL) is an automatic exit instruction. When your stock hits the SL price, the system closes your position immediately — no manual action needed. It\'s not optional. It\'s how you survive bad trades.',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '',
          slLabel: 'Stop Loss — auto-exits here',
          targetLabel: 'Target (₹)',
          highlight: 'sl',
        },
      },
      {
        title: 'Where to place your SL — not a random number',
        body: 'Your SL must go just below a key level that, if broken, proves your trade thesis wrong. For a BUY at a support: SL just below that support. For a breakout trade: SL below the breakout level. Never set SL as "I\'m comfortable losing ₹X" — it must be structural.',
        icon: 'Target',
        ui: {
          type: 'reasons',
          active: 'Below Support',
          reasons: ['Below Support', 'Below Breakout Level', 'Below Prev Day Low', 'ATR-based', 'Random ❌'],
        },
      },
      {
        title: 'Entering SL in the order ticket',
        body: 'In the order ticket, scroll to the SL/Target fields. Type your SL price in the "Stop Loss (₹)" field. The risk panel below immediately shows your max loss in rupees. If it\'s more than 1–2% of your capital — cut quantity first.',
        icon: 'Edit',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          highlight: 'sl',
        },
      },
      {
        title: 'Setting a Target',
        body: 'In the Target (₹) field, enter the price where you\'ll book profit. The R:R ratio shows automatically. Rule: Target must be at least 2× the distance of your SL. If SL is 20 points away, target must be 40+ points away.',
        icon: 'TrendingUp',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '1,402.00',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.0',
          highlight: 'target',
        },
      },
      {
        title: 'The risk panel — read before every trade',
        body: 'Below the SL/Target fields is a live risk panel:\n• Green = risk within your profile limits\n• Orange = near your max risk per trade\n• Red = overexposed — reduce quantity or tighten SL\nNever place a trade when the panel shows red.',
        icon: 'BarChart2',
        ui: {
          type: 'quantity',
          qty: 10,
          capital: '₹13,630',
          limitPrice: 'Risk: ₹220 · within limit ✓',
        },
      },
      {
        title: 'Place the trade — SL is tracked automatically',
        body: 'Once placed, TradeReflex tracks your SL in real time against the sim-date price feed. When price hits SL, your position auto-closes and P&L is recorded. You can see active SL levels on the Orders tab.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY 10 Shares — RELIANCE',
          color: '#16C784',
          status: 'Open · SL active at ₹1,340 · Target ₹1,402',
        },
        cta: { label: 'Place a trade →', path: '/trade' },
      },
    ],
  },

  {
    id: 'trade-planning',
    label: 'Trade\nPlanning',
    color: '#16C784',
    zone: 'trading',
    position: { lat: -38, lon: -150 },
    steps: [
      {
        title: 'What is Trade Planning?',
        body: 'Trade Planning is a structured pre-trade checklist that walks you through every decision before you place a single order. Go to Discover → Trade Planning. Work through each step in order — don\'t skip.',
        icon: 'Map',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['Global Cues', 'Market Regime', 'Stock Selection', 'Entry Defined', 'Size Calculated'],
        },
      },
      {
        title: 'Step 1 — Global cues (pre-9:15 AM)',
        body: 'Check Global Cues: SGX Nifty gap, US futures direction, crude oil price, Dollar Index, and FII/DII net flows from the previous session. These set your directional bias — bullish, bearish, or neutral — for the whole day.',
        icon: 'Globe',
        ui: {
          type: 'search',
          stock: 'SGX Nifty',
          price: '+0.42%',
          change: 'Gap up · favour longs today',
        },
      },
      {
        title: 'Step 2 — Market regime',
        body: 'Open Market Analysis. Is today a trend day, range day, or high-volatility day? A gap-up open followed by a strong first candle = trend day (trade breakouts). A gap that fills quickly = range day (trade reversals).',
        icon: 'Activity',
        ui: {
          type: 'regime-card',
          regime: 'Bullish Trend',
          strength: 'Moderate',
          advancing: 318,
          declining: 182,
        },
      },
      {
        title: 'Step 3 — Sector + stock selection via Scanner',
        body: 'Open Scanners. Filter by Swing or Intraday. Find stocks at key levels with high confluence scores. The stock must align with the day\'s regime — don\'t take a breakout trade on a range day.',
        icon: 'ScanLine',
        ui: {
          type: 'search',
          stock: 'HDFC Bank',
          price: '₹1,642.30',
          change: '+2.1% · breakout signal',
        },
      },
      {
        title: 'Step 4 — Define your trade before opening the ticket',
        body: 'Answer all five before you open the order ticket:\n1. Which stock?\n2. BUY or SELL?\n3. Entry price?\n4. Stop Loss level?\n5. Target + R:R?\n\nIf you can\'t answer all five — you\'re not ready. Come back when you can.',
        icon: 'FileText',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'HDFC Bank',
          price: '₹1,642.30',
          change: '+2.1%',
        },
      },
      {
        title: 'Step 5 — Size correctly using the Calculator',
        body: 'Use the Calculator (Discover → Calculator). Enter your account size, today\'s risk per trade %, and SL distance. It tells you exactly how many shares to buy. Never size based on gut feel.',
        icon: 'Calculator',
        ui: {
          type: 'quantity',
          qty: 8,
          capital: '₹13,138',
          limitPrice: 'entry ₹1,642, SL ₹1,618',
        },
      },
      {
        title: 'Step 6 — Place the trade with SL + Target already decided',
        body: 'Only now open the Trade tab. You already know: stock, direction, price, SL, target, quantity. Fill the ticket in under 30 seconds — all decisions were made in steps 1–5.',
        icon: 'CheckCircle',
        ui: {
          type: 'sltp',
          sl: '1,618.00',
          target: '1,690.00',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.0',
          highlight: 'target',
        },
        cta: { label: 'Go to Trade Planning →', path: '/trade-planning' },
      },
    ],
  },

  {
    id: 'orders',
    label: 'Orders\n& Positions',
    color: '#16C784',
    zone: 'trading',
    position: { lat:  20, lon: -125 },
    steps: [
      {
        title: 'Opening the Orders tab',
        body: 'Tap the Orders icon in the bottom navigation bar. You\'ll see two tabs: Open (active positions and pending orders) and Closed (completed trades). All positions are sim-date based.',
        icon: 'FileText',
        ui: {
          type: 'reasons',
          active: 'Open',
          reasons: ['Open', 'Closed'],
        },
      },
      {
        title: 'Reading an open position',
        body: 'Each open row shows: stock symbol, product type (Intraday/Swing), quantity, entry price, current price, and unrealised P&L (green = profit, red = loss). Your SL and target are attached and tracked automatically.',
        icon: 'Activity',
        ui: {
          type: 'sltp',
          sl: '1,340.00',
          target: '1,402.00',
          slLabel: 'SL (active)',
          targetLabel: 'Target',
          rr: '1 : 2.0',
          highlight: 'target',
        },
      },
      {
        title: 'Pending limit orders',
        body: 'Limit orders that haven\'t filled yet show as "Pending". They fill automatically when the sim price reaches your limit. Capital is reserved from the moment you place them — even before they fill.',
        icon: 'Calendar',
        ui: {
          type: 'placebutton',
          label: 'TCS · LIMIT BUY · 3 shares',
          color: '#6366f1',
          status: 'Pending — waiting for ₹3,850',
        },
      },
      {
        title: 'Modifying a position',
        body: 'Tap any open position to open its detail. You can update your Stop Loss or Target price as the trade develops — for example, trail your SL up as price moves in your favour to lock in profits.',
        icon: 'Edit',
        ui: {
          type: 'sltp',
          sl: '1,355.00',
          target: '1,402.00',
          slLabel: 'Trailing SL (updated)',
          targetLabel: 'Target',
          rr: '1 : 2.8',
          highlight: 'sl',
        },
      },
      {
        title: 'Exiting a position',
        body: 'Tap Exit on any active position to close it at the current sim price. Exits are immediate — the final P&L is booked and the trade moves to your Closed tab and Trade History.',
        icon: 'TrendingUp',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Entry Price',  value: '₹1,362.96', positive: false },
            { label: 'Exit Price',   value: '₹1,393.96', positive: true  },
            { label: 'Qty',          value: '10 shares',  positive: false },
            { label: 'Booked P&L',   value: '+₹314',      positive: true  },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Cancelling a pending order',
        body: 'For unfilled limit orders, tap Cancel to remove them. Your reserved capital is released immediately back to your daily headroom. Always cancel stale limit orders at end of day — don\'t leave open orders you\'ve forgotten about.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'RELIANCE · LIMIT BUY · 5 shares',
          color: '#6366f1',
          status: 'Cancelled · ₹6,700 released back to daily headroom',
        },
        cta: { label: 'Go to Orders →', path: '/orders' },
      },
    ],
  },

  {
    id: 'markets',
    label: 'Markets',
    color: '#16C784',
    zone: 'trading',
    position: { lat: -10, lon: -120 },
    steps: [
      {
        title: 'What the Markets page actually shows',
        body: 'Tap Discover → Markets. The page lists all NSE stocks available in the simulation — symbol, company name, sector tag, live sim price, and % change for the day. There are no index cards or heatmaps here; this is your stock directory. Use it to browse or search before picking a trade candidate.',
        icon: 'BarChart2',
        ui: {
          type: 'search',
          stock: 'RELIANCE',
          price: '₹2,845.60',
          change: '+1.24%',
        },
      },
      {
        title: 'Search any stock by name or symbol',
        body: 'The search bar at the top filters the entire stock list in real time. Type "HDFC", "Infosys", or "NIFTY" — matching results appear instantly. Each row shows the symbol, full company name, sector label, current sim price, and today\'s % move.',
        icon: 'Search',
        ui: {
          type: 'search',
          stock: 'HDFCBANK',
          price: '₹1,672.30',
          change: '-0.84%',
        },
      },
      {
        title: 'Reading a stock row — price and % change',
        body: 'Each row: symbol on the left, company name and sector tag below it, price and % change on the right. Green % = up today, red % = down. The price shown is the current sim price — it ticks live during market hours (9:15–15:30 on the sim date).',
        icon: 'TrendingUp',
        ui: {
          type: 'trade-card-list',
          trades: [
            { symbol: 'RELIANCE',  type: 'BUY', product: 'Energy',   pnl: '₹2,845.60', pnlPct: '+1.24%', entry: '2845.60', exit: '', qty: 0, date: 'sim live', tags: ['Energy'],    note: 'Tap the row to open the full stock detail — chart, order book, F&O chain, and direct BUY/SELL buttons.' },
            { symbol: 'INFY',      type: 'BUY', product: 'IT',       pnl: '₹1,482.15', pnlPct: '+0.63%', entry: '1482.15', exit: '', qty: 0, date: 'sim live', tags: ['IT'],        note: 'Tap to open StockDetailPage — you get the full chart with TradingView and the order ticket pre-filled with the stock.' },
            { symbol: 'HDFCBANK',  type: 'BUY', product: 'Banking',  pnl: '₹1,672.30', pnlPct: '-0.84%', entry: '1672.30', exit: '', qty: 0, date: 'sim live', tags: ['Banking'],   note: 'Red % change means the stock is down today. Tap to see if there is a support level nearby before considering a trade.' },
          ],
        },
      },
      {
        title: 'Use sector tags to narrow your search',
        body: 'Every stock row shows its sector (IT, Banking, Energy, Pharma, Auto, etc.). Use this to narrow your hunt — if the Market Analysis page shows IT regime is bullish, search Markets for IT stocks showing positive % change. Sector alignment + stock setup = stronger trade.',
        icon: 'PieChart',
        ui: {
          type: 'tagsconfidence',
          activeTags: ['IT', 'Banking'],
          tags: ['IT', 'Banking', 'Energy', 'Pharma', 'Auto', 'FMCG', 'Metals', 'Infra'],
          confidence: 0,
        },
      },
      {
        title: 'Tap a stock → full detail and order ticket',
        body: 'Tap any stock row to open the Stock Detail page — full TradingView chart, real-time price, F&O chain access, and a direct BUY/SELL button that pre-fills the order ticket. This is the fastest path from browsing to placing a trade.',
        icon: 'CheckCircle',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'RELIANCE',
          price: '₹2,845.60',
          change: '+1.24%',
        },
        cta: { label: 'Go to Markets →', path: '/markets' },
      },
    ],
  },

  // ── LEARN zone ────────────────────────────────────────────────────────────────

  {
    id: 'act-practice',
    label: 'ACT\nPractice',
    color: '#f97316',
    zone: 'learn',
    position: { lat:  42, lon:  -90 },
    steps: [
      {
        title: 'What is ACT Practice?',
        body: 'ACT (Adaptive Chart Training) shows you real historical trade setups from NSE stocks and scores how well you analyse and decide. Go to Discover → ACT Practice. It\'s not about whether the trade made money — it\'s about whether your process was right.',
        icon: 'Zap',
        ui: { type: 'act-scenario', mode: 'decision' },
      },
      {
        title: 'Daily targets — Swing and Intraday',
        body: 'You have two daily targets: 5 Swing scenarios and 5 Intraday scenarios. Progress bars at the top track today\'s count. Complete both every day — consistency builds pattern recognition faster than anything else.',
        icon: 'Target',
        ui: { type: 'act-scenario', mode: 'targets' },
      },
      {
        title: 'How a scenario works',
        body: 'Tap a card to open it. You\'ll see a real stock chart stopped at the decision point — before the outcome. Read the chart, check the context panel (volume, sector, regime), then decide: Buy, Sell, or Skip.',
        icon: 'BarChart2',
        ui: { type: 'act-scenario', mode: 'context' },
      },
      {
        title: 'The 5-question checklist before every decision',
        body: 'Before tapping Enter, Wait, or Skip — run through this checklist mentally. Every question must have a clear answer. If any one fails, the trade likely fails too. ACT scores you on process, not luck — this checklist IS the process.',
        icon: 'CheckSquare',
        ui: { type: 'act-scenario', mode: 'checklist' },
      },
      {
        title: 'How to fill the ACT form — step by step',
        body: 'After reading the chart and context panel, here is exactly how to fill in the form. Decision → Reason tags → SL + Target (if entering) → Conviction. Each field affects your score. Tap each element below to understand what to fill and why.',
        icon: 'Edit',
        ui: { type: 'act-scenario', mode: 'fill-form' },
      },
      {
        title: 'Scoring — process over outcome',
        body: 'After you decide, the chart reveals what happened next. Your score is based on: did you identify the correct chart pattern? Was your SL placement logical? Was your R:R above 1:2? A correct decision with bad process scores lower than a wrong decision with good process.',
        icon: 'Award',
        ui: { type: 'act-scenario', mode: 'score' },
      },
      {
        title: 'Reviewing your history',
        body: 'Tap History at the top to see all your past ACT decisions with scores. Look for patterns: are you consistently wrong on one setup type? Are you skipping too often? This tells you exactly what to study next in the Learning module.',
        icon: 'CheckCircle',
        ui: { type: 'act-scenario', mode: 'history' },
        cta: { label: 'Go to ACT Practice →', path: '/act' },
      },
    ],
  },

  {
    id: 'learning-path',
    label: 'Learning\nPath',
    color: '#f97316',
    zone: 'learn',
    position: { lat:  10, lon:  -75 },
    steps: [
      {
        title: 'The 13-level curriculum',
        body: 'Tap Discover → Learning. TradeReflex has a 13-level structured curriculum: Foundation (what markets are) → Markets (how indices and stocks work) → Trading (orders, SL, R:R) → F&O → Risk Management. Each level unlocks after you complete the previous.',
        icon: 'BookOpen',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Foundation',       check: 'What markets are, how prices move, NSE basics',                done: true,  detail: 'Level 1 covers the building blocks every trader needs: what a stock is, how exchanges work, what NIFTY 50 represents. Locked until you answer the checkpoint questions.' },
            { label: 'Markets',          check: 'Indices, sectors, breadth, market cap',                       done: true,  detail: 'Level 2 teaches you to read the market before picking a stock. Understanding NIFTY vs BANKNIFTY, sector rotation, and breadth prevents you from buying stocks against the market.' },
            { label: 'Trading',          check: 'Orders, SL, R:R, position sizing',                           done: false, detail: 'Level 3 is the core trading mechanics — this is where most traders fail. Market vs Limit orders, mandatory SL, calculating R:R before entry, sizing from risk budget.' },
            { label: 'F&O',              check: 'Options, futures, premium decay, Greeks',                    done: false, detail: 'Level 4 introduces derivatives: CE vs PE, futures margin, theta decay, strike selection. Do NOT trade F&O before completing this level.' },
            { label: 'Risk Management',  check: 'Position sizing, drawdown control, capital tiers',           done: false, detail: 'Level 5 ties everything together: how much to risk per trade, how to size correctly from SL distance, and how TradeReflex\'s capital tier system protects you.' },
          ],
        },
      },
      {
        title: 'Opening a level',
        body: 'Tap any unlocked level tile. Inside you\'ll see a list of lessons for that level. Each lesson covers exactly one concept — theory, a visual explanation, and a checkpoint question you must answer to continue.',
        icon: 'Map',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Levels Unlocked',   value: '4 of 13', positive: true  },
            { label: 'Lessons Done',       value: '18 of 47', positive: true  },
            { label: 'Current Level',      value: 'Trading',  positive: true  },
            { label: 'Checkpoints Passed', value: '18',       positive: false },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Checkpoint questions',
        body: 'At the end of each lesson is a multiple-choice checkpoint. You must answer correctly to mark the lesson complete. If you get it wrong, re-read the lesson — the question tests comprehension, not memory.',
        icon: 'Brain',
        ui: {
          type: 'reasons',
          active: 'Risk:Reward must be ≥1:2',
          reasons: ['SL should be a round number', 'Risk:Reward must be ≥1:2', 'Always trade Intraday', 'Set SL only for losers'],
        },
      },
      {
        title: 'Level progress bar',
        body: 'Each level shows a progress bar (e.g. "4 of 7 lessons complete"). Finish all lessons in a level to unlock the next. Your progress is saved — you can leave and return any time.',
        icon: 'BarChart2',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Trading Level',  value: '4 / 7',   positive: true  },
            { label: 'F&O Level',      value: 'Locked',  positive: false },
            { label: 'Risk Mgmt',      value: 'Locked',  positive: false },
            { label: 'Total Progress', value: '38%',     positive: true  },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Connect learning to the app',
        body: 'At the bottom of key lessons you\'ll see "Try this in the app →" links that bring you directly to the relevant feature — Scanner, Trade ticket, Capital page — so you can immediately apply what you just learned.',
        icon: 'CheckCircle',
        ui: {
          type: 'reasons',
          active: 'Try placing a Limit Order →',
          reasons: ['Open Scanners →', 'Try placing a Limit Order →', 'Check Capital Tier →', 'Open Derivatives →'],
        },
        cta: { label: 'Go to Learning →', path: '/learning' },
      },
    ],
  },

  {
    id: 'trade-journal',
    label: 'Trade\nJournal',
    color: '#f97316',
    zone: 'learn',
    position: { lat: -20, lon:  -90 },
    steps: [
      {
        title: 'Your trade journal in TradeReflex',
        body: 'Every trade you place is automatically journalled. Tap Discover → Trade History. Every trade — open and closed — is logged with entry, exit, P&L, product type, trade reason, technical tags, confidence score, and outcome.',
        icon: 'History',
        ui: {
          type: 'trade-card-list',
          trades: [
            { symbol: 'RELIANCE', type: 'BUY', product: 'Intraday', pnl: '+₹840', pnlPct: '+6.2%', entry: '₹1,362.96', exit: '₹1,446.96', qty: 10, date: 'Today, 09:47', tags: ['Breakout', 'Volume Spike'], note: 'Opened on morning breakout, exited at target.' },
            { symbol: 'HDFCBANK', type: 'SELL', product: 'Intraday', pnl: '-₹320', pnlPct: '-1.8%', entry: '₹1,680.00', exit: '₹1,648.00', qty: 5, date: 'Yesterday', tags: ['RSI'], note: 'SL hit — valid setup, outcome was bad luck.' },
            { symbol: 'NIFTY CE', type: 'BUY', product: 'Delivery',  pnl: '+₹2,250', pnlPct: '+30.3%', entry: '₹148.50', exit: '₹193.50', qty: 1, date: '2 days ago', tags: ['Trendline', 'Support'], note: 'Held for target, closed before expiry.' },
          ],
        },
      },
      {
        title: 'Reading a trade record',
        body: 'Each row shows: symbol, date, direction, entry/exit price, P&L in ₹ and %, your chosen trade reason, and confidence score. Green P&L = winner. Red = loser. But also look at the reason — a profitable "impulse" trade is worse for your development than a losing "technical" trade.',
        icon: 'FileText',
        ui: {
          type: 'trade-card-list',
          trades: [
            { symbol: 'INFY',     type: 'BUY',  product: 'Intraday', pnl: '+₹510', pnlPct: '+3.4%', entry: '₹1,510.00', exit: '₹1,561.00', qty: 3, date: 'Mon',      tags: ['Technical', 'MACD'],     note: 'Strategy-based entry, confident 8/10.' },
            { symbol: 'SBIN',     type: 'BUY',  product: 'Intraday', pnl: '-₹490', pnlPct: '-2.1%', entry: '₹620.00',   exit: '₹606.00',   qty: 8, date: 'Mon',      tags: ['Impulse ❌'],             note: 'Entered without a plan — impulse trade.' },
            { symbol: 'TATAMOTORS', type: 'SELL', product: 'Swing',  pnl: '+₹1,800', pnlPct: '+9.0%', entry: '₹900.00',   exit: '₹981.00',   qty: 10, date: 'Last week', tags: ['Resistance', 'Breakout'], note: 'Planned swing, held for 4 days.' },
          ],
        },
      },
      {
        title: 'Tap a trade for full detail',
        body: 'Tap any row to open the trade detail page. You see the price chart with exact entry and exit marked, your SL and target levels, the full reason + tags, and how your confidence matched the outcome.',
        icon: 'BarChart2',
        ui: {
          type: 'trade-card-list',
          trades: [
            { symbol: 'RELIANCE', type: 'BUY', product: 'Intraday', pnl: '+₹840', pnlPct: '+6.2%', entry: '₹1,362.96', exit: '₹1,446.96', qty: 10, date: 'Today, 09:47', tags: ['Breakout', 'Volume Spike', 'Support', 'Trendline'], note: 'Entry at 09:47 on morning breakout with volume confirmation. SL at 1,340. Target 1,386 (1:2.1 R:R). Confidence 8/10.' },
          ],
        },
      },
      {
        title: 'Finding your patterns — filter by reason tag',
        body: 'The Trade History page lets you scroll and visually spot clusters: same reason tag? Same time of day? Same setup type? Tap the reason chip on any losing trade — if "Impulse" appears repeatedly, that\'s your problem. The journal surfaces this because every trade is tagged at entry.',
        icon: 'Brain',
        ui: {
          type: 'trade-card-list',
          trades: [
            { symbol: 'SBIN',      type: 'BUY', product: 'Intraday', pnl: '-₹490', pnlPct: '-2.1%', entry: '₹620.00', exit: '₹606.00', qty: 8,  date: 'Mon 14:22', tags: ['Impulse ❌'],  note: 'No plan, entered after already being down ₹800 that day — classic revenge trade. The Impulse tag is the flag to look for.' },
            { symbol: 'TATAMOTORS', type: 'BUY', product: 'Intraday', pnl: '-₹380', pnlPct: '-1.8%', entry: '₹900.00', exit: '₹877.00', qty: 5, date: 'Tue 14:45', tags: ['Impulse ❌'], note: 'Second Impulse trade in 2 days — both losses, both after 2 PM. Pattern: revenge trading in the last hour. Use this to set a rule: no new trades after 2 PM following a loss.' },
          ],
        },
      },
      {
        title: 'Journal feeds Trade Analytics and Discipline',
        body: 'Every trade you log here automatically feeds two other pages: Trade Analytics (win rate, R:R, equity curve, best/worst setups) and your Discipline Score (reason quality, SL adherence, impulse %). Your journal is the raw data for your entire trading development system. Log every trade, every time.',
        icon: 'CheckCircle',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Trade logged',        check: 'Entry, exit, reason, tags, confidence',     done: true,  detail: 'Every field you fill in the order ticket (reason, tags, confidence) saves to your trade history automatically. You never need to manually log — just place trades with discipline.' },
            { label: 'Trade Analytics',     check: 'Win rate, R:R, equity curve, setup stats',  done: true,  detail: 'Go to Discover → Trade Analytics to see aggregated performance. The quality of these stats depends entirely on how well you tagged your trades at entry. Impulse trades with no reason distort the analytics.' },
            { label: 'Discipline Score',    check: 'Reason quality and SL adherence scored',    done: true,  detail: 'Your Discipline Score is calculated from your trade log. SL violations, impulse trades, and stop-moving are all detected automatically from the journal data.' },
            { label: 'Trade Performance',   check: 'Behavior profile built from history',       done: false, detail: 'Go to Discover → Trade Performance for your behavioral classification (Disciplined Executor, Momentum Chaser, etc.) — built entirely from your journal patterns.' },
          ],
        },
        cta: { label: 'Go to Trade History →', path: '/trade-history' },
      },
    ],
  },

  {
    id: 'strategy-feed',
    label: 'Strategy\nFeed',
    color: '#f97316',
    zone: 'learn',
    position: { lat:  42, lon:  -55 },
    steps: [
      {
        title: 'What the forum actually is',
        body: 'Tap Discover → Strategy Feed (routes to /forum). This is a community discussion board — not a structured setup-sharing tool. Posts are free-form text with a lifecycle tag. Tap the lifecycle chips below to see what each category is used for.',
        icon: 'Users',
        ui: {
          type: 'reasons',
          active: 'trade_idea',
          reasons: ['Trade Idea', 'Doubt', 'Backtest', 'Profit', 'Loss', 'Mistake'],
        },
      },
      {
        title: 'Reading a post — what you actually see',
        body: 'Each post has: a lifecycle flair (Trade Idea / Doubt / Profit / Loss / Mistake / Backtest), author name, time, post title, description, instrument tags, and upvote + comment count. Posts can also have data attachments — a P&L snapshot, discipline score, or backtest result pinned to the post.',
        icon: 'FileText',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'nyx',  text: '📌 Trade Idea · Vignesh · 2h ago\n\nHDFC Bank bounce from weekly support at ₹1,650. RSI turning up from 38. Plan: entry ₹1,655, SL ₹1,625, target ₹1,740. 5-day swing. Open to counter-arguments.' },
            { role: 'user', text: '⚠️ Doubt · Rohan · 1h ago\n\nThat support has been tested 3 times already — fourth test is statistically weaker. IV is elevated too. Would tighten SL or skip entirely.' },
          ],
          prompts: ['Trade Idea', 'Doubt', 'Profit', 'Loss', 'Mistake', 'Backtest'],
        },
      },
      {
        title: 'Creating a post — tap the + button',
        body: 'Tap the + button (top right) to open the post editor. Write a title and description — free-form text. Pick a lifecycle tag that fits: Trade Idea for a setup you\'re planning, Doubt for a question, Profit/Loss to share a completed trade, Mistake to document a lesson.',
        icon: 'Edit',
        ui: {
          type: 'reasons',
          active: 'Loss',
          reasons: ['Trade Idea', 'Doubt', 'Backtest', 'Profit', 'Loss', 'Mistake'],
        },
      },
      {
        title: 'Commenting and threading',
        body: 'Tap any post to open the full thread. Add a comment — agree, challenge, or ask for the SL logic. The best learning happens in threads where someone challenges the original poster\'s thesis with data. Upvote posts whose process is disciplined, not just those whose trades made money.',
        icon: 'Brain',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'user', text: 'Good analysis but — you said "regime is bullish" without checking breadth. Advancing stocks today: 312, declining: 604. The regime looks more sideways. Would reconsider the swing entry.' },
            { role: 'nyx',  text: 'Valid point on breadth. That changes the conviction. Will reduce size and tighten SL to ₹1,638 instead of ₹1,625.' },
          ],
          prompts: ['Challenge the thesis', 'Ask for SL logic', 'Share similar setup', 'Agree with plan'],
        },
      },
      {
        title: 'Learn from process, not outcomes',
        body: 'Follow posters whose reasoning is consistently structured — not those who made money on a single trade. A well-documented Loss post is more valuable than a vague Profit post. The forum\'s lifecycle tags are designed to track the full journey: Idea → Doubt → Trade → Profit or Loss → Mistake.',
        icon: 'CheckCircle',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Trade Idea',  check: 'Setup posted with thesis and levels',   done: true,  detail: 'A structured trade idea post includes: what you\'re watching, why, the key level, and what would invalidate the setup. Vague bullish/bearish calls have no learning value.' },
            { label: 'Doubt',       check: 'Community challenged the thesis',        done: true,  detail: 'Someone flagged the support test count and breadth data. This is the forum working as intended — peer review before you commit capital.' },
            { label: 'Trade taken', check: 'Position opened after incorporating feedback', done: true, detail: 'Adjusted SL based on community input. Reduced size. This is what separates forum use as a learning tool vs a tips channel.' },
            { label: 'Loss posted', check: 'Documented what actually happened',     done: false, detail: 'Whether profit or loss — post the result with what you learned. The Loss and Mistake tags are the most valuable for the community and for your own review.' },
          ],
        },
        cta: { label: 'Go to Strategy Feed →', path: '/forum' },
      },
    ],
  },

  {
    id: 'trade-performance',
    label: 'Trade\nPerformance',
    color: '#f97316',
    zone: 'learn',
    position: { lat: -20, lon:  -55 },
    steps: [
      {
        title: 'Your trader personality profile',
        body: 'Tap Discover → Trade Performance. This page analyses your actual trade history and classifies your trading personality — are you a Disciplined Executor, a Momentum Chaser, a Revenge Trader, or a Hesitant Analyst? Based purely on your data.',
        icon: 'PieChart',
        ui: {
          type: 'behavior-profile',
          score: 72,
          profile: 'Disciplined Executor',
          trade_count: 34,
          metrics: [
            { label: 'Patience',         score: 8, note: 'You wait for entries — 91% of trades entered within planned zone.' },
            { label: 'SL Discipline',    score: 7, note: 'Only 2 SL violations in 34 trades. Keep up the habit.' },
            { label: 'Consistency',      score: 7, note: 'Setup types are focused: 78% Breakout + Technical.' },
            { label: 'Emotional Control', score: 6, note: 'After a loss, next trade has 24% lower confidence avg — watch this.' },
          ],
        },
      },
      {
        title: 'Profile components',
        body: 'Your profile is built from: entry timing (do you chase or wait?), hold duration (do you cut winners short?), loss behaviour (do you double-down after a loss?), and reason distribution (what % of trades are impulse vs planned?).',
        icon: 'BarChart2',
        ui: {
          type: 'behavior-profile',
          score: 48,
          profile: 'Momentum Chaser',
          trade_count: 22,
          metrics: [
            { label: 'Patience',          score: 4, note: '62% of entries were chased — entered after the move, not before.' },
            { label: 'SL Discipline',     score: 5, note: '6 SL violations. Moving SL away from entry is a common pattern.' },
            { label: 'Consistency',       score: 5, note: 'High mix of Impulse + Technical + Tip — spread too thin.' },
            { label: 'Emotional Control', score: 4, note: 'Post-loss trades show increased position size — revenge pattern detected.' },
          ],
        },
      },
      {
        title: 'Behaviour metrics breakdown',
        body: 'Below the profile you see individual metrics scored 1–10:\n• Patience — do you wait for the entry or chase?\n• Discipline — do you stick to SL?\n• Consistency — same setup types or random?\n• Emotional control — how do you trade after a loss?\nEach metric shows your score and what to improve.',
        icon: 'Target',
        ui: {
          type: 'behavior-profile',
          score: 61,
          profile: 'Hesitant Analyst',
          trade_count: 18,
          metrics: [
            { label: 'Patience',          score: 9, note: 'You wait perfectly — but sometimes miss entries by being too selective.' },
            { label: 'SL Discipline',     score: 8, note: 'Only 1 SL violation. Strong habit.' },
            { label: 'Consistency',       score: 5, note: 'Trade frequency is low — only 18 trades. Need more data.' },
            { label: 'Emotional Control', score: 4, note: 'After losses you stop trading for 2–3 days. Work on re-entry process.' },
          ],
        },
      },
      {
        title: 'Consistency score',
        body: 'The consistency score measures whether your wins and losses follow a pattern (good) or are random (bad). High consistency with positive results = you have an edge. High consistency with negative results = consistent mistake — easier to fix than random mistakes.',
        icon: 'Activity',
        ui: {
          type: 'behavior-profile',
          score: 55,
          profile: 'Momentum Chaser',
          trade_count: 28,
          metrics: [
            { label: 'Consistency',      score: 8, note: '78% of trades use Breakout or Technical setup — focused approach. Consistent setups = consistent results.' },
            { label: 'Win Rate',         score: 6, note: '62% win rate is solid. Pair it with R:R > 1.5 and you have a profitable edge over time.' },
            { label: 'Avg R:R',          score: 6, note: 'Current R:R is 1:1.9. Close to 2:1 target. Even at 40% win rate this system makes money.' },
            { label: 'Impulse Trades',   score: 3, note: '18% of trades were impulse-tagged. Each impulse trade degrades your score. 0% impulse = maximum score.' },
          ],
        },
      },
      {
        title: 'Use it as a weekly review',
        body: 'Every Friday, open Trade Performance. Pick one metric that\'s below 6. Spend the next week focused on improving just that behaviour. Don\'t try to fix everything at once — compound improvement metric by metric.',
        icon: 'CheckCircle',
        ui: {
          type: 'behavior-profile',
          score: 74,
          profile: 'Disciplined Executor',
          trade_count: 41,
          metrics: [
            { label: 'This Week Focus',   score: 7, note: 'Emotional Control — 1 revenge trade detected on Tuesday. No more after loss.' },
            { label: 'Last Week Focus',   score: 8, note: 'SL Discipline — improved from 5 → 8 after consciously setting SL before entry.' },
            { label: 'Overall Trend',     score: 7, note: 'Behavior score up 12 points in 4 weeks. Keep reviewing Fridays.' },
            { label: 'Next Focus',        score: 6, note: 'Patience — 23% of trades still entered slightly late. Improve entry timing.' },
          ],
        },
        cta: { label: 'Go to Trade Performance →', path: '/trade-performance' },
      },
    ],
  },

  // ── F&O zone ──────────────────────────────────────────────────────────────────

  {
    id: 'buy-call',
    label: 'Buy CE\n(Call)',
    color: '#f59e0b',
    zone: 'fo',
    position: { lat:  42, lon:  -25 },
    steps: [
      {
        title: 'Step 1 — Pre-trade analysis (non-negotiable)',
        body: 'Before touching F&O: Check Global Cues (SGX Nifty direction), Market Regime (bullish/bearish), and the options chain OI data. Only buy a CE (Call) if the regime is bullish and market breadth supports it. Random CE buying destroys capital fast.',
        icon: 'Globe',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['SGX Green', 'Regime Bullish', 'FII Buying', 'OI Supportive', 'Low IV'],
        },
      },
      {
        title: 'Step 2 — Open Derivatives and select underlying',
        body: 'Tap Discover → Derivatives. The page has an underlying selector at the top (NIFTY 50 / BANKNIFTY / stock) and expiry tabs below it. Choose your underlying first, then pick the expiry — weekly or monthly. Weekly CEs cost less premium but decay faster. Monthly gives more time but costs more.',
        icon: 'Layers',
        ui: {
          type: 'reasons',
          active: 'NIFTY 50',
          reasons: ['NIFTY 50', 'BANKNIFTY', 'RELIANCE', 'HDFCBANK', 'TCS'],
        },
      },
      {
        title: 'Step 3 — Choose your CE strike',
        body: 'CE columns are on the left side of the options chain. Strike selection matters:\n• ATM (At The Money) — nearest strike to current price. Most liquid, moderate cost.\n• OTM (Out of The Money) — cheaper but needs a bigger move to profit.\n• ITM (In The Money) — expensive but safer delta.\nFor beginners: ATM only. Tap the strike row to open the order ticket.',
        icon: 'Target',
        ui: {
          type: 'reasons',
          active: 'ATM — Nearest Strike',
          reasons: ['ITM (In The Money)', 'ATM — Nearest Strike', 'OTM 1 step', 'OTM 2 steps ❌'],
        },
      },
      {
        title: 'Step 4 — Read the premium and confirm capital cost',
        body: 'The premium shown is the price per unit. Cost = Premium × Lot Size (NIFTY lot = 50). So ₹148.50 premium × 50 = ₹7,425 total capital locked.\n\nAlso check IV (Implied Volatility) — high IV means expensive premium. Buy CE only in low-to-normal IV. High IV = wait.',
        icon: 'BarChart2',
        ui: {
          type: 'quantity',
          qty: 1,
          capital: '₹7,425',
          limitPrice: '148.50 × 50 units',
        },
      },
      {
        title: 'Step 5 — Tap BUY on the CE',
        body: 'Tap the BUY button next to your chosen CE strike and expiry. The order ticket opens with premium auto-filled. Your capital check runs automatically — buying a CE requires full premium × lots as margin.',
        icon: 'ShoppingCart',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'NIFTY 22000 CE',
          price: '₹148.50',
          change: '+12.3%',
        },
      },
      {
        title: 'Step 6 — Set Stop Loss on the premium (not on NIFTY)',
        body: 'Set your SL on the premium price — not on where NIFTY is. If you bought at ₹148.50, set SL at ₹89 (40% stop). Max loss = ₹89 × 50 = ₹4,450. For swing CE trades, SL at 30–50% of premium paid is standard. Never hold a CE that has lost 50%+ hoping to "recover".',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '89.00',
          target: '220.00',
          slLabel: 'SL on premium (₹)',
          targetLabel: 'Target premium (₹)',
          rr: '1 : 2.1',
          highlight: 'sl',
        },
      },
      {
        title: 'Step 7 — Add reason, tags, confidence and place',
        body: 'Select "Technical" or "Strategy" as trade reason. Add tags (Breakout, Trendline). Set confidence 7+ — options decay every day so only enter at high conviction. Tap place and monitor from the Orders tab.',
        icon: 'CheckCircle',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['Strategy', 'Scanner', 'Technical', 'Fundamental', 'Impulse'],
        },
      },
      {
        title: 'Step 8 — Confirm and monitor',
        body: 'Tap BUY to place. Your CE order is now live. Monitor from the Orders tab — if premium drops to your SL level the position auto-exits. Check daily: theta (time decay) erodes value every day even if NIFTY doesn\'t move.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY 1 Lot (50 units)',
          color: '#f59e0b',
          status: 'Open — CE live · theta decay active',
        },
        cta: { label: 'Go to Derivatives →', path: '/derivatives' },
      },
    ],
  },

  {
    id: 'buy-put',
    label: 'Buy PE\n(Put)',
    color: '#f59e0b',
    zone: 'fo',
    position: { lat: -15, lon:  -25 },
    steps: [
      {
        title: 'Step 1 — Confirm bearish conditions first',
        body: 'Buying a PE (Put) is a bearish bet. Before opening Derivatives: check Global Cues for negative FII flow or weak US close, confirm Market Regime shows bearish or distribution, and see that index is below key moving averages. Don\'t buy PEs in a bull trend.',
        icon: 'Globe',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['SGX Red', 'Regime Bearish', 'FII Selling', 'Below 200 EMA', 'High IV caution'],
        },
      },
      {
        title: 'Step 2 — Open Derivatives and select underlying',
        body: 'Tap Discover → Derivatives. Select your underlying from the top selector — NIFTY, BANKNIFTY, or a weak stock showing relative underperformance. Choose expiry. For directional bearish trades, use the current week expiry (if 3+ days away) or next week\'s expiry.',
        icon: 'Layers',
        ui: {
          type: 'reasons',
          active: 'NIFTY 50',
          reasons: ['NIFTY 50', 'BANKNIFTY', 'RELIANCE', 'HDFCBANK', 'TCS'],
        },
      },
      {
        title: 'Step 3 — Select your PE strike',
        body: 'PE columns are on the right side of the options chain. For a bearish trade:\n• ATM PE — nearest to current price, maximum delta, most responsive\n• Slightly OTM PE — cheaper, needs more movement, higher leverage\nBeginners: stick to ATM or 1 strike OTM. Never go deep OTM.',
        icon: 'Target',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'NIFTY 21900 PE',
          price: '₹132.00',
          change: '+18.4%',
        },
      },
      {
        title: 'Step 4 — Check IV, then set SL on the premium',
        body: 'High IV means expensive premiums — IV crush can kill your PE value even if NIFTY falls. Buy PEs only in low-to-normal IV environments.\n\nOnce you place the buy, set your SL at 40–50% of premium paid. If you bought at ₹132, SL at ₹79. Target at ₹198 gives 1:2 R:R. Set SL before anything else.',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '79.00',
          target: '198.00',
          slLabel: 'Stop Loss on premium (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.0',
          highlight: 'sl',
        },
      },
      {
        title: 'Step 5 — Add reason, confidence and place',
        body: 'Select "Technical" or "Strategy" as trade reason. Set confidence 7+ — only buy options at high conviction, they decay every day. Tap BUY to place. Full premium × lots is your max loss — no more, no less.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY 1 Lot (50 units)',
          color: '#f59e0b',
          status: 'Open — PE premium live · max loss ₹6,600',
        },
        cta: { label: 'Go to Derivatives →', path: '/derivatives' },
      },
    ],
  },

  {
    id: 'buy-futures',
    label: 'Buy\nFutures',
    color: '#f59e0b',
    zone: 'fo',
    position: { lat:  10, lon:   -8 },
    steps: [
      {
        title: 'What is a Futures contract?',
        body: 'A Future is an agreement to buy or sell an asset at a future date at today\'s agreed price. In TradeReflex you trade NIFTY, BANKNIFTY, and stock futures. Key difference from options: futures have unlimited loss potential, so SL is not optional — it\'s survival.',
        icon: 'Layers',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'NIFTY FUT',
          price: '₹22,045',
          change: '+0.42%',
        },
      },
      {
        title: 'Step 1 — Pre-trade analysis is even more critical for futures',
        body: 'Futures have no premium decay but full directional risk. Check Global Cues, Market Regime, and SGX Nifty direction. Also check the Futures basis (future price − spot price) — a high positive basis means longs are expensive. Only trade when regime is clear.',
        icon: 'Globe',
        ui: {
          type: 'reasons',
          active: 'technical',
          reasons: ['SGX Green', 'Regime Bullish', 'Basis Normal', 'FII Buying', 'Clear Trend'],
        },
      },
      {
        title: 'Step 2 — Open Derivatives → Futures tab',
        body: 'Tap Discover → Derivatives then switch to the Futures tab. Select your underlying from the selector at the top (NIFTY, BANKNIFTY, or a stock). Choose the expiry month — near-month futures are most liquid. The lot size for NIFTY is 50 units.',
        icon: 'Calendar',
        ui: {
          type: 'reasons',
          active: 'NIFTY 50',
          reasons: ['NIFTY 50', 'BANKNIFTY', 'RELIANCE', 'HDFCBANK', 'TCS'],
        },
      },
      {
        title: 'Step 3 — Understand the margin',
        body: 'Futures require only 10% of notional as margin. NIFTY at ₹22,000 × 50 lot = ₹11 lakh notional → only ₹1.1L margin. But a 1% adverse move = ₹11,000 loss. That\'s 10% of your margin in one move. This is why SL placement is everything.',
        icon: 'Wallet',
        ui: {
          type: 'quantity',
          qty: 1,
          capital: '₹22,045',
        },
      },
      {
        title: 'Step 4 — Tap BUY and set SL immediately',
        body: 'Tap BUY on the futures contract. In the ticket, set a strict SL — typically below the nearest support or a key technical level, not more than 0.5–0.8% below entry for intraday. Set your target at 1.5–2× the SL distance. Never trade a future without SL.',
        icon: 'Shield',
        ui: {
          type: 'sltp',
          sl: '21,820',
          target: '22,280',
          slLabel: 'Stop Loss (₹)',
          targetLabel: 'Target (₹)',
          rr: '1 : 2.1',
          highlight: 'sl',
        },
      },
      {
        title: 'Step 5 — Place and monitor',
        body: 'Select trade reason, add confidence (futures = high conviction only, so 7+), and swipe to place. Monitor the Orders tab actively. Futures MTM (mark to market) is calculated every minute — know your max loss before you enter, not after.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'BUY NIFTY FUT (1 lot)',
          color: '#f59e0b',
          status: 'Open — position live, MTM updating',
        },
        cta: { label: 'Go to Derivatives →', path: '/derivatives' },
      },
    ],
  },

  {
    id: 'capital-tier',
    label: 'Capital\n& Tier',
    color: '#f59e0b',
    zone: 'fo',
    position: { lat: -42, lon:   -8 },
    steps: [
      {
        title: 'What is the Capital Tier system?',
        body: 'TradeReflex limits how much capital you can deploy per day based on your Discipline Score. Better process = higher tier = more capital. It protects you from over-trading when your discipline is low. Your score today determines how much you can trade tomorrow.',
        icon: 'Wallet',
        ui: {
          type: 'limits-panel',
          limits: [
            { label: 'Minimum  (score 0–34)',   value: '₹50K/day',   color: '#ef4444', detail: 'Impulsive tier — score is too low to deploy significant capital. Focus on building process: always use SL, log reasons, journal every trade.' },
            { label: 'Restricted (score 35–49)', value: '₹75K/day',  color: '#f97316', detail: 'Unstructured tier — improving but inconsistent. Getting score above 50 unlocks Standard tier and ₹25K more per day.' },
            { label: 'Standard  (score 50–64)',  value: '₹1L/day',   color: '#f59e0b', detail: 'Developing tier — consistent enough to deploy ₹1L/day. Your Discipline Score today sets your cap for tomorrow.' },
            { label: 'Advanced  (score 65–79)',  value: '₹1.5L/day', color: '#6366f1', detail: 'Consistent tier — disciplined execution over many trades earns this. ₹1.5L/day is sufficient for most retail strategies.' },
            { label: 'Elite     (score 80+)',    value: '₹2L/day',   color: '#16C784', detail: 'Disciplined tier — top 10% of traders on the platform. Maximum daily cap. Score reflects that your process is consistently excellent.' },
          ],
        },
      },
      {
        title: 'First 5 trades — the grace period',
        body: 'Before your first 5 trades are placed, there is no Discipline Score to evaluate — so the tier system gives you a fixed ₹1L/day cap (Standard tier) for your first 5 trades. This lets you build a trade history before the scoring kicks in. After trade #5, your real Discipline Score takes over.',
        icon: 'Award',
        ui: {
          type: 'limits-panel',
          limits: [
            { label: 'Trades 1–5 (grace period)', value: '₹1L/day', color: '#6366f1', detail: 'Fixed Standard-tier cap regardless of score. You have no Discipline Score yet so the system gives you Standard by default. Use these trades well — they will form the foundation of your score.' },
            { label: 'Trade 6+ (score-based)',     value: 'Tier by score', color: '#16C784', detail: 'From trade 6 onwards, your daily cap is determined by yesterday\'s Discipline Score. Score below 35 = Minimum (₹50K). Score 80+ = Elite (₹2L). Improve your process every day to unlock more capital.' },
          ],
        },
      },
      {
        title: 'How capital is counted per trade',
        body: 'The calculation depends on what you\'re trading:\n\n• Equity: full order value (price × quantity)\n• Futures: only 10% of notional (just the margin)\n• Buying CE/PE: full premium × lots\n• Writing CE/PE (short options): full premium locked as margin\n\nFutures use far less of your daily cap than equity for the same exposure.',
        icon: 'Layers',
        ui: {
          type: 'limits-panel',
          limits: [
            { label: 'Equity (e.g. RELIANCE × 10)',      value: '₹13,630',  color: '#6366f1', detail: 'Full order value counted: Price × Quantity. Buying ₹1,363 × 10 shares = ₹13,630 deducted from your daily cap.' },
            { label: 'Futures (NIFTY FUT, 1 lot)',        value: '₹22,045',  color: '#f59e0b', detail: 'Only 10% margin counted: NIFTY ₹22,045 × 50 lot = ₹11L notional but only ₹22,045 (10%) deducted from daily cap. Massive leverage with controlled cap usage.' },
            { label: 'Buy CE/PE (NIFTY 22000 CE, 1 lot)', value: '₹7,425',  color: '#16C784', detail: 'Full premium × lot size counted: ₹148.50 × 50 = ₹7,425. This is your max loss too — options cannot lose more than the premium you paid.' },
            { label: 'Cover Order (closing a short)',       value: '₹0 cap', color: '#16C784', detail: 'Cover orders (buying back a CE/PE you wrote) bypass the capital check entirely. You can always close a position even at your daily limit.' },
          ],
        },
      },
      {
        title: 'Cover orders skip the capital check',
        body: 'If you\'re closing an existing short option position (buying back a CE/PE you wrote), that is a Cover Order. Cover orders bypass the daily capital limit check — you can always close a position even if you\'re at your daily limit. You can never be locked into a losing position.',
        icon: 'Shield',
        ui: {
          type: 'placebutton',
          label: 'COVER ORDER — Close short CE',
          color: '#f59e0b',
          status: 'Capital check bypassed · cover orders always allowed',
        },
      },
      {
        title: 'Check your current cap',
        body: 'Go to Discover → Capital to see: your current tier, today\'s capital used, remaining headroom, and a full breakdown of your Behaviour Score components. Use this before planning your trades for the day.',
        icon: 'CheckCircle',
        ui: {
          type: 'placebutton',
          label: 'View Capital Dashboard',
          color: '#f59e0b',
          status: 'Standard Tier · ₹1L daily cap · ₹34,200 used today',
        },
        cta: { label: 'Go to Capital →', path: '/capital' },
      },
    ],
  },

  // ── ANALYSIS zone ─────────────────────────────────────────────────────────────

  {
    id: 'scanner',
    label: 'Scanners',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat:  48, lon:   48 },
    steps: [
      {
        title: 'What Scanners do',
        body: 'Tap Discover → Scanners. The scanner runs across all NSE stocks on the sim date and surfaces stocks forming specific chart patterns or crossing technical thresholds — in seconds, not hours. Use it every morning before you pick any stock.',
        icon: 'ScanLine',
        ui: {
          type: 'search',
          stock: 'RELIANCE',
          price: '₹1,362.96',
          change: '+2.1% · Breakout · Confluence: 4/5',
        },
      },
      {
        title: 'Patterns tab — chart formations',
        body: 'Shows stocks forming recognisable chart formations:\n• Breakout — price crossing resistance with volume\n• Pullback — retracing in an uptrend to support\n• Reversal — downtrend ending at a key level\n• Continuation — flag/pennant in a trend\n\nToggle Swing vs Intraday at the top to filter by your trade style.',
        icon: 'BarChart2',
        ui: {
          type: 'tagsconfidence',
          tags: ['Breakout', 'Pullback', 'Reversal', 'Continuation'],
          activeTags: ['Breakout'],
          confidence: 8,
        },
      },
      {
        title: 'Signals tab — technical triggers',
        body: 'Shows real-time technical signal fires: MACD crossover, RSI reversal, Volume Spike, Trendline break. The Confluence filter shows only stocks where multiple signals align simultaneously — these are the strongest setups. Only trade 3+ confluence.',
        icon: 'Zap',
        ui: {
          type: 'reasons',
          active: 'scanner',
          reasons: ['MACD Cross', 'RSI Reversal', 'Volume Spike', 'Trendline Break', 'EMA Cross'],
        },
      },
      {
        title: 'Reading a scanner card',
        body: 'Each card shows: stock name, pattern/signal found, current price, key levels (support/resistance), timeframe, and a confluence score (1–5). Higher confluence = more signals aligning = stronger trade candidate. Don\'t trade low-confluence signals.',
        icon: 'FileText',
        ui: {
          type: 'tagsconfidence',
          tags: ['3/5 Confluence', '4/5 Confluence', '5/5 Confluence'],
          activeTags: ['4/5 Confluence', '5/5 Confluence'],
          confidence: 9,
        },
      },
      {
        title: 'From scanner to trade',
        body: 'Tap any scanner card to open that stock\'s detail page with the chart. Verify the setup visually — does the chart tell the same story as the scanner? If yes, tap BUY/SELL directly from the stock detail. The symbol is pre-filled in the order ticket.',
        icon: 'CheckCircle',
        ui: {
          type: 'buysell',
          active: 'BUY',
          stock: 'HDFC Bank',
          price: '₹1,642.30',
          change: '+2.1% · Scanner: Breakout',
        },
        cta: { label: 'Go to Scanners →', path: '/scanners' },
      },
    ],
  },

  {
    id: 'analytics',
    label: 'Trade\nAnalytics',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat:  20, lon:   62 },
    steps: [
      {
        title: 'What Trade Analytics shows',
        body: 'Tap Discover → Trade Analytics. This aggregates every trade you\'ve placed into performance metrics: win rate, average R:R, P&L over time, best setups, worst setups, and an equity curve. This is where patterns in your trading data become visible.',
        icon: 'TrendingUp',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Total P&L',    value: '+₹12,840', positive: true  },
            { label: 'Win Rate',     value: '48%',       positive: true  },
            { label: 'Avg R:R',      value: '1.9×',      positive: true  },
            { label: 'Total Trades', value: '34',        positive: false },
            { label: 'Best Setup',   value: 'Breakout',  positive: true  },
            { label: 'Avg Drawdown', value: '−₹820',     warn: true      },
          ],
          showCurve: true,
        },
      },
      {
        title: 'Performance overview panel',
        body: 'At the top: Total P&L (₹), Win Rate (%), Total Trades, and Average R:R. A win rate of 40% with R:R of 2:1 is more profitable than a win rate of 60% with R:R of 0.8:1. Always read win rate alongside R:R — never alone.',
        icon: 'BarChart2',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Win Rate',     value: '48%',       positive: true  },
            { label: 'Avg R:R',      value: '1.9×',      positive: true  },
            { label: 'Best Setup',   value: 'Breakout',  positive: true  },
            { label: 'Avg Drawdown', value: '−₹820',     warn: true      },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Timeframe filter',
        body: 'Switch between 7D, 30D, 90D, and All Time. Use 30-day windows for trend spotting — too short and it\'s noise, too long and recent changes are hidden. Compare your last 30 days against the previous 30 days.',
        icon: 'Calendar',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Win Rate',     value: '48%',  positive: true },
            { label: 'Total Trades', value: '34',   positive: false },
            { label: 'Avg R:R',      value: '1.9×', positive: true },
            { label: 'Total P&L',    value: '+₹12,840', positive: true },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Win vs loss size analysis',
        body: 'The win/loss size chart shows your average winning trade vs average losing trade in ₹. Your average win must be larger than your average loss. If avg win < avg loss, even a 60% win rate will lose money long term. Fix this before everything else.',
        icon: 'Activity',
        ui: {
          type: 'analytics-grid',
          metrics: [
            { label: 'Avg R:R',      value: '1.9×',  positive: true },
            { label: 'Avg Drawdown', value: '−₹820', warn: true     },
            { label: 'Best Setup',   value: 'Breakout', positive: true },
            { label: 'Total P&L',    value: '+₹12,840', positive: true },
          ],
          showCurve: false,
        },
      },
      {
        title: 'Equity curve — the real truth',
        body: 'The equity curve shows your cumulative P&L over time. A smooth upward line = consistent edge. A line that spikes and crashes = erratic, luck-dependent trading. Your goal is not a high ending value — it\'s a smooth, steadily rising curve without big drawdowns.',
        icon: 'CheckCircle',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Flat start',        check: 'First 5 trades — calibration phase',              done: true,  detail: 'Your first few trades build the base. The curve is flat or slightly noisy here. This is normal — the system is reading your patterns. Focus on process, not P&L.' },
            { label: 'Consistent edge',   check: 'Curve rising steadily — win rate + R:R working',  done: true,  detail: 'A smooth upward curve means your edge is repeatable. Wins are consistent, losses are controlled. This is your goal: not just profit, but predictable profit.' },
            { label: 'Drawdown period',   check: 'Curve dips — review your last 10 trades',         done: false, detail: 'Every trader has drawdown periods. A good trader caps the drawdown at 10–15% of peak. If yours is deeper, pause and review: is it random variance or a process mistake?' },
            { label: 'Recovery & scale',  check: 'Curve recovers — add capital only after recovery', done: false, detail: 'Don\'t add capital during drawdown — you\'re trying to make back losses, which leads to bigger mistakes. Scale up only when the curve has recovered and is making new highs.' },
          ],
        },
        cta: { label: 'Go to Analytics →', path: '/trading-analytics' },
      },
    ],
  },

  {
    id: 'market-analysis',
    label: 'Market\nRegime',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat: -10, lon:   48 },
    steps: [
      {
        title: 'What is Market Regime?',
        body: 'Tap Discover → Mkt Regime. This tells you the current market environment — Bullish Trend, Bearish Trend, Sideways/Range, or High Volatility. Your entire playbook changes based on the regime. Trading the wrong strategy in the wrong regime is the #1 cause of losses.',
        icon: 'Activity',
        ui: {
          type: 'regime-card',
          regime: 'Bullish Trend',
          strength: 'Moderate',
          advancing: 312,
          declining: 188,
        },
      },
      {
        title: 'Reading the regime card',
        body: 'The top card shows: current regime, strength (weak/moderate/strong), and which strategies work best in this regime. Bullish Trend = breakouts and pullbacks to support. Bearish Trend = shorts and resistance rejections. Sideways = range fades. High Volatility = reduce size or wait.',
        icon: 'TrendingUp',
        ui: {
          type: 'regime-card',
          regime: 'Sideways/Range',
          strength: 'Weak',
          advancing: 248,
          declining: 252,
        },
      },
      {
        title: 'Market breadth',
        body: 'Breadth shows advancing stocks vs declining stocks. Strong regime + strong breadth = high conviction, trade aggressively. Strong regime + weak breadth = narrow leadership, be selective. Opposite regime and breadth = regime change incoming, step back.',
        icon: 'BarChart2',
        ui: {
          type: 'regime-card',
          regime: 'Bullish Trend',
          strength: 'Strong',
          advancing: 390,
          declining: 110,
        },
      },
      {
        title: 'Sector rotation',
        body: 'Which sectors are flowing with money today? IT leading in a bull regime = tech-driven rally (often sustained). Banking leading = broad-based rally (healthier). When only 1-2 sectors lead and the rest are flat, the rally is fragile — size down.',
        icon: 'PieChart',
        ui: {
          type: 'reasons',
          active: 'IT',
          reasons: ['IT', 'Banking', 'Energy', 'Pharma', 'Auto', 'FMCG'],
        },
      },
      {
        title: 'Make this your first screen every morning',
        body: 'Open Market Regime before looking at any individual stock. Write down: today\'s regime, supporting strategy types, and sectors to focus on. This 2-minute check prevents you from taking a breakout trade on a range day.',
        icon: 'CheckCircle',
        ui: {
          type: 'regime-card',
          regime: 'High Volatility',
          strength: 'Strong',
          advancing: 210,
          declining: 290,
        },
        cta: { label: 'Go to Market Analysis →', path: '/market-analysis' },
      },
    ],
  },

  {
    id: 'backtest',
    label: 'Backtest\nLab',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat:  20, lon:   88 },
    steps: [
      {
        title: 'What is Backtesting — and why it matters',
        body: 'Backtesting means testing your trading strategy on real historical price data before risking any real capital. Instead of guessing if a strategy works, you prove it first. Think of it as a flight simulator — you can crash a hundred times without losing a rupee. Open Discover → Backtest Lab to start.',
        icon: 'FlaskConical',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Define rules',        check: 'Entry, exit, SL, universe — all measurable', done: false, detail: 'A backtest is only as good as the rules you write. Vague rules ("stock looks bullish") cannot be tested. Every condition must be specific enough that a computer can evaluate it.' },
            { label: 'Run on history',      check: 'Test on 2+ years of real NSE data',          done: false, detail: 'TradeReflex backtests run on the same real fo_candles data used in the simulation — real NSE 1-minute candles. No synthetic data. Results reflect actual historical price behavior.' },
            { label: 'Validate out-of-sample', check: 'Confirm edge on data strategy never saw', done: false, detail: 'Train on 2014–2020, test on 2020–2024. If performance holds on data the strategy never saw, you have real edge. If it collapses, the original results were overfitting.' },
            { label: 'Go live in sim',      check: 'Run in Algo Flows for 30 days before real capital', done: false, detail: 'Backtest → Live Sim → Real capital. Never skip the sim step. Live execution always differs from backtest due to slippage, gaps, and missed signals. The sim closes the gap.' },
          ],
        },
      },
      {
        title: 'Step 1 — Write your entry rule (be specific)',
        body: 'Tap "New Backtest". The first field is Entry Condition. This is the exact trigger for when the strategy buys.\n\nExample: "Price crosses above 20-day high AND Volume > 1.5× 20-day average."\n\nVague rules like "stock looks bullish" cannot be backtested. Every condition must be measurable. The more specific, the more useful the result.',
        icon: 'Target',
        ui: {
          type: 'strategy-builder',
          sections: [
            { label: 'Entry Conditions (ALL must be true)', type: 'entry', conditions: ['Price > 20D High', 'Volume > 1.5× Avg', 'RSI < 30'] },
            { label: 'Exit Conditions (first to trigger exits)', type: 'exit', conditions: ['RSI > 70', 'SL hit', 'Target hit'] },
          ],
        },
      },
      {
        title: 'Step 2 — Define your Stop Loss and Target rule',
        body: 'After entry, you need exit rules. In the SL field enter how you calculate your stop loss — fixed %, ATR-based, or previous day low. In the Target field set your reward multiple.\n\nExample: SL = "Previous day low" · Target = "2× SL distance"\n\nThis gives you a minimum 1:2 R:R on every backtest trade automatically.',
        icon: 'Shield',
        ui: {
          type: 'reasons',
          active: 'Prev Day Low',
          reasons: ['Prev Day Low', '1% Fixed', '1× ATR', 'Support Level'],
        },
      },
      {
        title: 'Step 3 — Choose your universe and date range',
        body: 'Universe = which stocks does the strategy scan?\n\n• NIFTY 50 only — large caps, most liquid, cleanest signals\n• NIFTY 500 — broader, more trades but more noise\n• Custom list — specific sectors (e.g. only IT stocks)\n\nDate range: test at least 2 full years. One good year might be luck. Two years across different market regimes (bull, bear, sideways) proves real edge.',
        icon: 'ScanLine',
        ui: {
          type: 'strategy-builder',
          sections: [
            { label: 'Universe (stocks to scan)', type: 'universe', conditions: ['All NSE stocks'] },
            { label: 'Position Sizing', type: 'sizing', conditions: ['1% of capital/trade'] },
          ],
        },
      },
      {
        title: 'Step 4 — Run the backtest and read the results',
        body: 'Tap Run. Results appear in 10–30 seconds across all historical setups. The most important thing to read is NOT just the total profit — read Win Rate + Avg R:R together, Max Drawdown, and Profit Factor.\n\nTap any metric card below to understand what it means and whether this strategy has a real edge.',
        icon: 'BarChart2',
        ui: {
          type: 'backtest-result',
          metrics: [
            { label: 'Win Rate',     value: '45%',   positive: true },
            { label: 'Avg R:R',      value: '2.1×',  positive: true },
            { label: 'Profit Factor',value: '1.8',   positive: true },
            { label: 'Max Drawdown', value: '−18%',  warn: true },
            { label: 'Total Trades', value: '87',    positive: false },
            { label: 'Sharpe Ratio', value: '1.42',  positive: true },
          ],
        },
      },
      {
        title: 'Step 5 — Identify what is failing',
        body: 'If results are poor, isolate the problem:\n\n• Low win rate (< 35%) + low R:R = your entry is wrong. Tighten entry conditions.\n• Good win rate but large drawdown = your SL is too loose. Tighten it.\n• Good metrics but only 20 trades = strategy fires too rarely. Broaden the universe.\n• Profit Factor < 1.0 = losing strategy no matter what. Rethink the core idea.\n\nChange ONE variable at a time, then re-run. Never change everything at once.',
        icon: 'Activity',
        ui: {
          type: 'backtest-result',
          metrics: [
            { label: 'Win Rate',     value: '28%',  warn: true     },
            { label: 'Avg R:R',      value: '1.4×', positive: false },
            { label: 'Profit Factor',value: '0.8',  warn: true     },
            { label: 'Max Drawdown', value: '−34%', warn: true     },
            { label: 'Total Trades', value: '18',   positive: false },
            { label: 'Sharpe Ratio', value: '0.4',  warn: true     },
          ],
        },
      },
      {
        title: 'Step 6 — The danger of overfitting (critical lesson)',
        body: 'Overfitting means tweaking your strategy until it looks perfect on the data you tested — but fails on new data. Signs you are overfitting:\n\n• You\'ve run 50+ variations and keep adjusting until results look good\n• Strategy only works on a specific 6-month window\n• Too many conditions (5+ AND conditions)\n\nFix: Test on 2014–2020, then validate on 2020–2024 separately. If performance holds — real edge. If it collapses — overfitting.',
        icon: 'AlertCircle',
        ui: {
          type: 'overfit-card',
          signs: [
            { label: '50+ variations run',    body: 'If you keep tweaking until results look good, you are fitting the strategy to noise, not finding a real edge. The more you adjust, the more the results represent past luck rather than a repeatable pattern.' },
            { label: 'Only works one window', body: 'A real edge works across multiple market regimes — bull, bear, and sideways. If your strategy only works in 2020–2021 bull market data, it\'s not a strategy — it\'s luck in a bull market.' },
            { label: '5+ AND conditions',     body: 'Too many conditions = strategy fires only on perfect setup days. In practice, it may trigger 3–4 times per year. That\'s not enough trades to trust the edge, and you miss most of the moves.' },
          ],
          fixes: [
            'Test 2014–2020 (in-sample). Validate on 2020–2024 (out-of-sample).',
            'If out-of-sample performance is <50% as good as in-sample → overfitting.',
            'Use fewer, stronger conditions. Simple rules survive regime changes better.',
          ],
        },
      },
      {
        title: 'Step 7 — Graduate to live sim',
        body: 'When your backtest shows a Profit Factor > 1.5, Win Rate + R:R work together, Max Drawdown is acceptable, and results hold across multiple years — activate it in Algo Flows.\n\nLive sim is the next test. Reality always differs from backtest (slippage, gaps, missed signals). Run it live for 30 days before trusting it with serious capital. The gap between backtest and live = your next lesson.',
        icon: 'CheckCircle',
        ui: {
          type: 'live-sim',
          stages: [
            { label: 'Backtest confirmed',   check: 'Profit Factor > 1.5, Drawdown acceptable', done: true,  detail: 'Your numbers hold up. The edge is statistically likely to be real. But backtest data is clean — live data has gaps, slippage, and emotional noise.' },
            { label: 'Out-of-sample passed', check: 'Results hold on data the strategy never saw', done: true,  detail: 'You tested on 2014–2020, validated on 2020–2024. Both periods show consistent results. This is the most important quality check — if it passed this, you have a real edge.' },
            { label: 'Live sim (30 days)',   check: 'Activate in Algo Flows, no real money yet', done: false, detail: 'Run the strategy live in sim mode for 30 days. Track every signal — did the strategy fire when expected? Did prices fill at the expected levels? Slippage + signal drift = live underperformance is normal.' },
            { label: 'Deploy with real capital', check: 'Start small — 25% of planned capital', done: false, detail: 'Start with 25% of your planned capital. If live results match sim within 15%, scale up. If there\'s big divergence — investigate before adding more capital. The goal is to close the gap between backtest and live reality.' },
          ],
        },
        cta: { label: 'Go to Backtest Lab →', path: '/backtest' },
      },
    ],
  },

  {
    id: 'algo-flows',
    label: 'Algo\nFlows',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat: -10, lon:   95 },
    steps: [
      {
        title: 'What is Algo Flows — and what you see first',
        body: 'Open Discover → Algo Flows. The first screen shows your dashboard — 4 summary stats at the top, then your strategy list below.\n\nAlgo Flows lets you build rule-based strategies that automatically scan the market and place paper trades. You define the rules once; the engine evaluates them every minute during market hours.\n\nIt is NOT a magic money machine. Algo Flows automates execution — it does not replace thinking.',
        icon: 'FlaskConical',
        ui: {
          type: 'algo-dashboard-summary',
          stats: [
            { key: 'active',  label: 'Active Strategies', value: '2',         note: 'Strategies currently running live — scanning stocks every minute and placing orders when conditions match.' },
            { key: 'capital', label: 'Capital Deployed',  value: '₹2,00,000', note: 'Total capital allocated across all active strategies. Each strategy has its own capital bucket — they do not share.' },
            { key: 'pnl',     label: 'Total P&L',         value: '+₹4,820',   pnl: true, note: 'Combined paper profit/loss across all strategies. Includes both open and closed positions.' },
            { key: 'trades',  label: 'Total Trades',      value: '34',         note: 'Total orders placed by all strategies. More trades = more data to validate whether your edge is real.' },
          ],
          strategies: [
            { name: 'RSI Bounce — NIFTY 50', status: 'ACTIVE', capital: '₹1,00,000', trades: 23, stocks: 3, discipline: 88, pnl: '+₹3,140', detail: 'Running 14 days. 23 trades, 48% win rate, 2.1 avg R:R — edge confirmed. Tap any strategy row to open its full detail panel.' },
            { name: '20D Breakout Swing',    status: 'PAUSED', capital: '₹1,00,000', trades: 11, stocks: 5, discipline: 61, pnl: '+₹1,680', detail: 'Paused — win rate dropped below 40% in the last 7 days. Sideways market killed the breakout edge. Will resume when regime turns bullish.' },
          ],
        },
      },
      {
        title: 'Step 1 — Create a strategy: Basic Info and Risk Settings',
        body: 'Tap "+ New Strategy" (top-right button). The modal has two sections.\n\nBasic Info: give it a clear name — "RSI Bounce NIFTY50" not "Strategy 1". A name you understand is a strategy you can evaluate.\n\nRisk Settings control how the engine sizes and limits trades:\n• Capital Allocated — the total capital pool for this strategy\n• Risk per Trade (%) — max % of capital to risk on one trade\n• Max Trades/Day — hard daily limit, prevents overtrading\n• Cooldown — minimum gap between trades on the same symbol',
        icon: 'Edit',
        ui: {
          type: 'strategy-create-form',
          fields: [
            { key: 'name', label: 'Strategy Name', required: true, value: 'RSI Bounce — NIFTY 50', note: 'Use a name that describes the logic: "RSI Bounce NIFTY50" tells you what it does and on what universe. "Strategy 1" tells you nothing — you will not remember what it does in 2 weeks.' },
            { key: 'desc', label: 'Description', required: false, placeholder: 'Describe your strategy...', note: 'Optional but useful — describe the core idea in one sentence. Forces you to articulate the logic, which catches vague thinking before it costs you money.' },
          ],
          riskFields: [
            { key: 'capital', label: 'Capital Allocated (₹)', value: '₹1,00,000', note: 'The capital pool for this strategy only. Other strategies have separate pools — they never share. Start with a small allocation until the strategy proves itself live.' },
            { key: 'risk',    label: 'Risk per Trade (%)',    value: '1%',         note: '1% risk means if capital = ₹1L, max loss per trade = ₹1,000. The engine auto-calculates quantity based on your SL distance. Never set above 2% per trade.' },
            { key: 'maxday', label: 'Max Trades / Day',      value: '3',          note: 'Hard cap on daily trades for this strategy. Prevents the algo from firing 20 times in one session if conditions are triggered repeatedly. 3–5 is a sensible starting point.' },
            { key: 'cool',   label: 'Cooldown',              value: '1 day',      note: 'Minimum time between trades on the same symbol. "1 day" means the algo will not re-enter a stock it just traded until the next market day — prevents rapid re-entry on noise.' },
          ],
        },
      },
      {
        title: 'Step 2 — Create a strategy: Engine Rules and Conditions',
        body: 'The Engine Rules section is where you define what triggers a trade.\n\nConditions are the real-time market signals your algo watches. Tap each one below to see what it checks.\n\nEngine Rules control how those conditions are evaluated:\n• Match Logic — ALL conditions must fire (stricter, fewer trades) or ANY one (looser, more trades)\n• Trend Filter — only trade in a bullish/bearish/any direction\n• Entry Mode — trade at live price (Instant) or wait for candle close\n• Stop Loss % and Target % — how the engine exits every trade automatically',
        icon: 'Target',
        ui: {
          type: 'strategy-engine-rules',
          conditions: [
            { name: 'RSI_OVERSOLD',          display: 'RSI Oversold',       dir: 'bullish', desc: 'RSI dropped below 30 — stock is in oversold territory and likely to bounce. This is your primary entry trigger. Never use alone — combine with a trend or breakout condition.' },
            { name: 'PRICE_ABOVE_SMA20',     display: 'Price > SMA 20',     dir: 'bullish', desc: 'Price is above its 20-day moving average — short-term uptrend confirmed. Prevents buying into a falling knife when RSI oversold fires during a strong downtrend.' },
            { name: 'VOLUME_SPIKE',          display: 'Volume Spike',       dir: 'bullish', desc: 'Today\'s volume is significantly above average — strong conviction behind the move. A breakout or bounce on high volume is far more reliable than one on thin volume.' },
            { name: 'MACD_BULLISH_CROSSOVER',display: 'MACD Bullish Cross', dir: 'bullish', desc: 'MACD line crossed above the signal line — classic momentum buy signal. Lags price by nature, so it confirms momentum rather than catching the exact low.' },
            { name: 'SUPERTREND_BUY',        display: 'SuperTrend Buy',     dir: 'bullish', desc: 'SuperTrend indicator flipped green — trend has turned bullish. Good filter to pair with an entry condition so you only buy when the broader trend supports the trade.' },
          ],
          engineFields: [
            { key: 'logic',  label: 'Match Logic',  value: 'ALL conditions',    color: 'indigo', note: 'ALL = every condition must be true simultaneously before buying. This is stricter — fewer trades but higher quality. ANY = one condition is enough — more trades, more noise.' },
            { key: 'trend',  label: 'Trend Filter', value: 'Any direction',     color: 'indigo', note: 'Filters trades by market direction. "Any" means the algo fires in bull and bear markets. Set to "Bullish only" if your conditions are designed for uptrends.' },
            { key: 'mode',   label: 'Entry Mode',   value: 'Instant (live price)', color: 'indigo', note: 'Instant = order placed at the live price when conditions fire. Candle Close = waits for the candle to close before entering. Instant is faster; Candle Close is more conservative.' },
            { key: 'sl',     label: 'Stop Loss',    value: '2%',               color: 'red',    note: 'The engine automatically places a stop loss at entry price − 2%. When price falls to this level, the position is closed. Never run a strategy without a stop loss.' },
            { key: 'target', label: 'Target',       value: '4%',               color: 'green',  note: 'The engine books profit when price rises 4% from entry. Combined with 2% SL, this gives a 2:1 R:R on every trade — the algo enforces your plan without emotion.' },
            { key: 'exit',   label: 'Exit Type',    value: 'SL / Target hit',  color: 'indigo', note: 'SL/Target hit = exits when either price level is reached. Time-based = closes position by 3:15 PM regardless. Use SL/Target for swing, Time-based for strict intraday.' },
          ],
        },
      },
      {
        title: 'Step 3 — Strategy detail: performance and configuration',
        body: 'Tap any strategy in the list to open its detail panel on the right.\n\nThe top section shows live performance — Win Rate, ROI, Wins/Losses, Open Trades. Tap each metric to understand what it means.\n\nBelow that is the full Configuration — every setting you defined when creating the strategy: capital, risk %, max trades/day, cooldown, which stocks it watches, which conditions it uses, and all engine rules.',
        icon: 'BarChart2',
        ui: {
          type: 'strategy-detail',
          performance: { win_rate: '48%', roi: '+3.1%', wl: '11 / 12', open_trades: '1' },
          config: {
            'Capital':        '₹1,00,000',
            'Risk/Trade':     '1%',
            'Max Trades/Day': '3',
            'Cooldown':       '1 day',
            'Match Logic':    'ALL conditions',
            'Stop Loss':      '2%',
            'Target':         '4%',
            'Entry Mode':     'Instant',
          },
          stocks:     ['RELIANCE', 'INFY', 'HDFCBANK'],
          conditions: ['RSI Oversold', 'Price > SMA 20', 'Volume Spike'],
        },
      },
      {
        title: 'Step 4 — Recent Decisions and Execution Log',
        body: 'Two panels give you full transparency into what the engine did and why.\n\nRecent Decisions shows every stock the algo evaluated in the last 24 hours — and whether it triggered, skipped, or was blocked (e.g. cooldown active, daily limit reached). This is how you verify the algo is reading your stocks correctly.\n\nExecution Log is the full audit trail — every evaluation cycle that resulted in an action, with the exact reason and price.',
        icon: 'Activity',
        ui: {
          type: 'strategy-decisions',
          decisions: [
            { symbol: 'RELIANCE', decision: 'triggered', reason: 'RSI Oversold ✓  Price > SMA 20 ✓  Volume Spike ✓ — all conditions met', cooldown: null, conditions: { met: ['RSI Oversold', 'Price > SMA 20', 'Volume Spike'], failed: [] } },
            { symbol: 'INFY',     decision: 'skipped',   reason: 'RSI Oversold ✓  Price > SMA 20 ✗ — only 1 of 3 conditions met (ALL required)', cooldown: null, conditions: { met: ['RSI Oversold'], failed: ['Price > SMA 20', 'Volume Spike'] } },
            { symbol: 'HDFCBANK', decision: 'blocked',   reason: 'Cooldown active — traded this symbol 6 hours ago', cooldown: '18h', conditions: null },
          ],
          logs: [
            { action: 'TRADE_TRIGGERED', symbol: 'RELIANCE', time: '10:14 AM', reason: 'All 3 conditions met at ₹2,847', detail: 'Entry: ₹2,847 · SL: ₹2,788 (−2%) · Target: ₹2,962 (+4%). Qty: 35 shares. Order placed at live price via Instant mode.' },
            { action: 'CONDITIONS_NOT_MET', symbol: 'INFY', time: '10:14 AM', reason: 'Price > SMA 20 not satisfied', detail: 'INFY is trading below its 20-day average — the trend condition failed. The algo correctly skipped the trade even though RSI was oversold.' },
            { action: 'COOLDOWN_ACTIVE', symbol: 'HDFCBANK', time: '10:14 AM', reason: 'Cooldown: 18h remaining', detail: 'HDFCBANK was traded at 9:52 AM yesterday. Cooldown = 1 day, so the algo will not re-enter until tomorrow morning. This prevents double-entry on the same setup.' },
          ],
        },
      },
      {
        title: 'Step 5 — Backtest before activating (non-negotiable)',
        body: 'Inside any strategy\'s detail, tap "Backtest". Select a stock from the strategy\'s universe, set a date range (minimum 1 year), and run. The engine replays your exact conditions on historical data.\n\nNever activate a strategy you haven\'t backtested. A live strategy with no historical validation is automated gambling.\n\nWhat to look for:\n• Return % — is it positive over the full period?\n• Win Rate — combined with avg R:R, does the math work?\n• Max Drawdown — can you psychologically hold through this?\n• Total Trades — under 30 trades means the result is noise, not edge',
        icon: 'FlaskConical',
        ui: {
          type: 'backtest-result',
          metrics: [
            { label: 'Win Rate',      value: '45%',   positive: true },
            { label: 'Avg R:R',       value: '2.1×',  positive: true },
            { label: 'Profit Factor', value: '1.8',   positive: true },
            { label: 'Max Drawdown',  value: '−18%',  warn: true },
            { label: 'Total Trades',  value: '87',    positive: false },
            { label: 'Sharpe Ratio',  value: '1.42',  positive: true },
          ],
        },
      },
      {
        title: 'Step 6 — Algo Discipline Score: don\'t interfere with your own system',
        body: 'Each strategy has its own Discipline Score — separate from your manual trading score. It measures algo interference: how much you tamper with the system after activating it.\n\nEvents that drop your score:\n• Panic Pause (−20): pausing the strategy while trades are open\n• Config Change (−10): tweaking conditions or settings while ACTIVE\n• Manual Override (−15): buying a stock manually when the algo said skip\n\nEvents that improve it:\n• Held Through Loss (+10): staying patient through a drawdown without pausing\n\nA score of 100 means you built a system and trusted it.',
        icon: 'Shield',
        ui: {
          type: 'algo-discipline',
          score: 75,
          level: 'GOOD',
          stats: { panic_pauses: 1, config_changes: 0, manual_overrides: 1, held_through_loss: 2 },
          events: [
            { type: 'HELD_THROUGH_LOSS',     icon: 'OK',  label: 'Held Through Loss',       time: '2 days ago', note: '+10 points. RSI Bounce had 3 consecutive losses but you did not panic-pause. The strategy recovered on day 4. This is exactly what the score rewards.' },
            { type: 'PANIC_PAUSE',           icon: '!',   label: 'Panic Pause',             time: '5 days ago', note: '−20 points. You paused the strategy while HDFCBANK had an open position. The trade was at −₹800 at the time but would have recovered to +₹400. Let the SL do its job.' },
            { type: 'CONFIG_CHANGE_ACTIVE',  icon: 'MAN', label: 'Manual Override',         time: '6 days ago', note: '−15 points. You manually bought RELIANCE after the algo skipped it (conditions not met). The stock fell 3% — the algo was right. Trust the conditions you defined.' },
          ],
        },
      },
      {
        title: 'Step 7 — When to pause, edit, or stop a strategy',
        body: 'Pause when:\n• 5+ consecutive losses — investigate before continuing\n• Market regime changes (bull strategy in a sideways market)\n• Algo firing on setups you wouldn\'t take manually\n\nEdit when:\n• Entry conditions are too broad (too many low quality trades)\n• SL is consistently hit before the setup plays out\n• Always re-backtest after editing\n\nStop when:\n• Profit Factor drops below 1.0 over 20+ live trades\n• Core idea is fundamentally broken for the current regime\n\nDo NOT panic-stop after 2–3 losses — that is normal variance, not edge failure.',
        icon: 'CheckCircle',
        ui: {
          type: 'algo-dashboard-summary',
          stats: [
            { key: 'active',  label: 'Active Strategies', value: '1',         note: 'One strategy paused, one stopped — only the proven strategy remains running.' },
            { key: 'capital', label: 'Capital Deployed',  value: '₹1,00,000', note: 'Capital in the stopped/paused strategies is not deployed — it sits idle until you reactivate or replace the strategy.' },
            { key: 'pnl',     label: 'Total P&L',         value: '+₹1,040',   pnl: true, note: 'Net P&L after the stopped strategy lost ₹2,100. The running strategy\'s gains partially offset it.' },
            { key: 'trades',  label: 'Total Trades',      value: '42',         note: '42 total trades across all 3 strategies. The stopped strategy accounts for 8 of those — all on the same flawed edge.' },
          ],
          strategies: [
            { name: 'RSI Bounce — NIFTY 50', status: 'ACTIVE', capital: '₹1,00,000', trades: 23, stocks: 3, discipline: 88, pnl: '+₹3,140', detail: 'Continue running — edge confirmed over 23 trades. Win rate (48%) and R:R (2.1×) match backtest.' },
            { name: '20D Breakout Swing',    status: 'PAUSED', capital: '₹1,00,000', trades: 11, stocks: 5, discipline: 61, pnl: '+₹1,680', detail: 'Paused — sideways market. Resume when regime turns bullish. Do not edit the conditions just because market changed.' },
            { name: 'EMA Cross Intraday',    status: 'DELETED', capital: '₹1,00,000', trades: 8, stocks: 2, discipline: 40, pnl: '−₹2,100', detail: 'Stopped and deleted. 8 losses, win rate 25% vs backtest 48% — the intraday EMA edge did not survive live market noise. Needs a full rebuild.' },
          ],
        },
        cta: { label: 'Go to Algo Flows →', path: '/algo-flows' },
      },
    ],
  },

  {
    id: 'global-cues',
    label: 'Global\nCues',
    color: '#06b6d4',
    zone: 'analysis',
    position: { lat: -38, lon:   70 },
    steps: [
      {
        title: 'Why global cues matter for Indian trading',
        body: 'Indian markets don\'t open in a vacuum. The overnight US session, Asian markets, commodity prices, and FII flows all directly impact where NIFTY opens and how it trades. Open Discover → Global Cues every morning before 9 AM.',
        icon: 'Globe',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'SGX Nifty',    value: '+0.62%',  direction: 'up'   },
            { label: 'S&P 500',      value: '+1.1%',   direction: 'up'   },
            { label: 'FII Flow',     value: '+₹2,180 Cr', direction: 'up' },
            { label: 'Crude Oil',    value: '+0.3%',   direction: 'neutral' },
          ],
        },
      },
      {
        title: 'SGX Nifty — the first number to check',
        body: 'SGX Nifty (Singapore Exchange NIFTY futures) trades overnight and is the real-time predictor of where NIFTY 50 will open. Gap up >0.5% = likely bullish open. Gap down >0.5% = cautious, wait for first 15 minutes. Gap >1.5% either way = volatile, reduce position size.',
        icon: 'TrendingUp',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'SGX Nifty (Gap Up)',   value: '+0.62%', direction: 'up'   },
            { label: 'SGX Nifty (Gap Down)', value: '−1.8%',  direction: 'down' },
          ],
        },
      },
      {
        title: 'US markets — S&P 500 and Nasdaq',
        body: 'A strong US close (+1%+) typically means a positive Indian open. A sharp US sell-off (-1.5%+) almost always drags Indian markets lower. But watch the reason — US rate fears impact India differently than a single company\'s earnings miss. Context matters.',
        icon: 'BarChart2',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'S&P 500', value: '+1.1%',  direction: 'up'   },
            { label: 'Nasdaq',  value: '+1.6%',  direction: 'up'   },
          ],
        },
      },
      {
        title: 'FII and DII flows',
        body: 'FII (Foreign Institutional Investors) flows move markets. Net FII buying with DII also buying = strong demand, favour longs. FII selling but DII buying = support holding, neutral to cautious. Both selling = avoid longs entirely. Check previous session\'s provisional data.',
        icon: 'Wallet',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'FII Flow', value: '+₹2,180 Cr', direction: 'up'   },
            { label: 'DII Flow', value: '+₹890 Cr',   direction: 'up'   },
          ],
        },
      },
      {
        title: 'Crude oil and Dollar Index',
        body: 'India imports 85% of its crude oil. Crude rising sharply = inflation pressure = bearish for Indian markets and especially aviation/paint/chemical stocks. A rising Dollar Index (DXY) weakens the rupee, pressuring FII outflows. Track both daily.',
        icon: 'Activity',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'Crude Oil',    value: '$87.2 / +0.8%', direction: 'up'     },
            { label: 'DXY (Dollar)', value: '104.2 / −0.2%', direction: 'neutral' },
          ],
        },
      },
      {
        title: 'Build your 2-minute morning routine',
        body: 'Every trading day before 9:15 AM: Open Global Cues → note SGX direction → check US close % → read FII/DII numbers → check crude and DXY. Then decide: bullish, bearish, or sit out. This 2-minute routine prevents 90% of wrong-direction trades.',
        icon: 'CheckCircle',
        ui: {
          type: 'cues-card',
          cues: [
            { label: 'SGX Nifty',    value: '+0.62%',       direction: 'up'     },
            { label: 'S&P 500',      value: '+1.1%',        direction: 'up'     },
            { label: 'FII Flow',     value: '+₹2,180 Cr',   direction: 'up'     },
            { label: 'DII Flow',     value: '+₹890 Cr',     direction: 'up'     },
            { label: 'Crude Oil',    value: '+0.3%',        direction: 'neutral' },
            { label: 'DXY (Dollar)', value: '−0.2%',        direction: 'neutral' },
          ],
        },
        cta: { label: 'Go to Global Cues →', path: '/global-cues' },
      },
    ],
  },

  // ── DISCIPLINE zone ───────────────────────────────────────────────────────────

  {
    id: 'discipline-score',
    label: 'Discipline\nScore',
    color: '#8b5cf6',
    zone: 'discipline',
    position: { lat:  42, lon:  120 },
    steps: [
      {
        // Slide 1 — Main Score Card + Peer Benchmark ("How You Compare")
        title: 'Your Discipline Score and where you rank',
        body: 'Tap Discover → Discipline. The score (0–100) sits on a 5-band spectrum — each band maps to a daily capital tier. Below it, "How You Compare" shows your anonymous percentile rank among all TradeReflex traders, your strongest component, and your weakest one.',
        icon: 'Target',
        ui: {
          type: 'discipline-score-card',
          score: 72,
          profile_type: 'Baseline Trader',
          percentile: 58,
          total_users: 1240,
          best:    { label: 'Stop Loss Discipline', percentile: 78 },
          weakest: { label: 'Trade Frequency',      percentile: 22 },
        },
      },
      {
        // Slide 2 — Trader Personality + Why This Label signals + Behavior Metrics grid
        title: 'Your trader personality and behavior metrics',
        body: 'Below the score card, the page shows your Trader Personality label — derived from your last 30 trades — and the behavioral signals that earned it. Then a 2×3 grid of raw behavior metrics. Tap any metric to understand what it measures and what a healthy number looks like.',
        icon: 'Brain',
        ui: {
          type: 'discipline-behavior',
          profile_type: 'Baseline Trader',
          profile_score: 72,
          confidence: 81,
          signals: [
            'Strategy-based trades make up 68% of your entries — solid process foundation.',
            'Revenge trade count is 3 this month — worth watching after losing streaks.',
            'Avg confidence 7.2/10 with 54% win rate — slightly overconfident. Calibrate tags.',
          ],
          metrics: [
            { label: 'Strategy Trades', value: '68%',    status: 'good', note: 'Trades entered from a defined setup vs impulse. 68%+ is healthy — most entries are planned, not reactive.' },
            { label: 'Impulse Trades',  value: '18%',    status: 'warn', note: 'Entries with no setup — tip, gut feel, FOMO. Above 30% hurts your score significantly.' },
            { label: 'Avg Confidence',  value: '7.2/10', status: 'good', note: 'Your average confidence tag at entry. Compare to win rate — if confidence >> win rate, you\'re overconfident.' },
            { label: 'Trades/Day',      value: '4.1',    status: 'good', note: 'Average trades per session. Above 8/day usually means overtrading. Your profile allows 5 max.' },
            { label: 'Win Rate',        value: '54%',    status: 'good', note: 'Percentage of closed trades that were profitable. Healthy when paired with R:R > 1.2. Never read alone.' },
            { label: 'Revenge Trades',  value: '3',      status: 'warn', note: 'Trades placed immediately after a loss without a setup. Even 1 per week is worth eliminating.' },
          ],
        },
      },
      {
        // Slide 3 — What's Costing / Making You Money (P&L behaviour buckets)
        title: 'What\'s actually costing and making you money',
        body: 'The page groups all trades into behaviour buckets — SL respected, no SL, planned exits, emotional exits — and shows net P&L per bucket. This is the most honest view of your edge. Tap any bucket to expand the individual trades inside it.',
        icon: 'TrendingUp',
        ui: {
          type: 'discipline-pnl-impact',
          total_trades: 42,
          buckets: [
            {
              key: 'sl_respected',
              label: 'SL Respected',
              pnl: 8400,
              count: 18,
              trades: [
                { symbol: 'NIFTY-24000-CE', exit_reason: 'SL',     confidence: 7, pnl: -1200, unlucky: true },
                { symbol: 'BANKNIFTY-FUT',  exit_reason: 'TARGET', confidence: 8, pnl: 3100 },
                { symbol: 'RELIANCE',        exit_reason: 'TARGET', confidence: 7, pnl: 2600 },
              ],
            },
            {
              key: 'planned',
              label: 'Planned Entry + Exit',
              pnl: 5200,
              count: 12,
              trades: [
                { symbol: 'HDFCBANK',       exit_reason: 'TARGET', confidence: 8, pnl: 2800 },
                { symbol: 'NIFTY-23500-PE', exit_reason: 'TARGET', confidence: 7, pnl: 1900 },
              ],
            },
            {
              key: 'no_sl',
              label: 'No SL Set',
              pnl: -4800,
              count: 7,
              trades: [
                { symbol: 'INFY',           exit_reason: 'MANUAL', confidence: 6, pnl: -2200 },
                { symbol: 'NIFTY-23800-CE', exit_reason: 'MANUAL', confidence: 5, pnl: -1600 },
              ],
            },
            {
              key: 'emotional',
              label: 'Emotional / Revenge',
              pnl: -3100,
              count: 5,
              trades: [
                { symbol: 'BANKNIFTY-CE', exit_reason: 'MANUAL', confidence: 4, pnl: -1800 },
                { symbol: 'NIFTY-FUT',   exit_reason: 'SL',     confidence: 5, pnl: 900, lucky: true },
              ],
            },
          ],
        },
      },
      {
        // Slide 4 — P&L by Time of Day + Confidence Calibration
        title: 'When you trade matters — and does confidence predict outcome?',
        body: 'The Intelligence Insights section answers two questions: which market hours are making or costing you money, and does your confidence tag actually predict whether you win? These patterns are invisible without data — this section surfaces them.',
        icon: 'Clock',
        ui: {
          type: 'discipline-insights',
          time_of_day: [
            { hour: 9,  label: '9:15',  avg_pnl: -820,  trade_count: 8  },
            { hour: 10, label: '10:00', avg_pnl: 1240,  trade_count: 11 },
            { hour: 11, label: '11:00', avg_pnl: 680,   trade_count: 7  },
            { hour: 13, label: '1:00',  avg_pnl: -340,  trade_count: 5  },
            { hour: 14, label: '2:00',  avg_pnl: 920,   trade_count: 6  },
            { hour: 15, label: '3:00',  avg_pnl: -1100, trade_count: 5  },
          ],
          confidence_insight: 'High-confidence trades (8–10) average +₹1,240 vs -₹380 for low-confidence — your instinct is calibrated. Trust 8+ setups more.',
          confidence_tiers: [
            { key: 'low',  label: 'Low Confidence (1–5)',  count: 9,  avg_pnl: -380,  total_pnl: -3420 },
            { key: 'mid',  label: 'Mid Confidence (6–7)',  count: 18, avg_pnl: 420,   total_pnl: 7560  },
            { key: 'high', label: 'High Confidence (8–10)',count: 15, avg_pnl: 1240,  total_pnl: 18600 },
          ],
        },
      },
      {
        // Slide 5 — Discipline by Market Regime + Trade Statistics
        title: 'How your discipline holds up in different market regimes',
        body: 'The page shows your average discipline score and P&L broken down by market regime — Bullish, Bearish, Sideways. This tells you if your process breaks down in specific market conditions. Most traders lose discipline in volatile or bearish sessions.',
        icon: 'BarChart2',
        ui: {
          type: 'discipline-regime',
          trade_stats: { total: 42, wins: 23, losses: 19 },
          regime_discipline: [
            { regime: 'BULLISH',  days: 12, avg_score: 78, total_pnl:  14200 },
            { regime: 'SIDEWAYS', days: 9,  avg_score: 69, total_pnl: -2100  },
            { regime: 'BEARISH',  days: 6,  avg_score: 58, total_pnl: -5800  },
          ],
        },
      },
      {
        // Slide 6 — Why Score Changed timeline + Recent Trades Impact
        title: 'Why your score moved and which trades drove it',
        body: 'Every score change is logged with the exact components that moved and the reason. Below that, Recent Trades Impact shows each trade\'s strategy tag, confidence, and whether you journaled it. Tap any score entry to expand the breakdown.',
        icon: 'Activity',
        ui: {
          type: 'discipline-why-changed',
          entries: [
            {
              delta: +4,
              score: 76,
              symbol: 'BANKNIFTY-FUT',
              time: 'Today, 3:28 PM',
              component_changes: [
                { label: 'Stop Loss Discipline', before: 85, after: 90, delta: +5 },
                { label: 'Risk-Reward Planning', before: 70, after: 74, delta: +4 },
              ],
              reasons: [
                { good: true,  text: 'SL set and respected on all 3 trades today.' },
                { good: true,  text: 'Target hit on BANKNIFTY before SL was moved.' },
              ],
            },
            {
              delta: -3,
              score: 72,
              symbol: 'NIFTY-CE',
              time: 'Yesterday, 2:15 PM',
              component_changes: [
                { label: 'Emotional Control', before: 65, after: 58, delta: -7 },
                { label: 'Trade Frequency',   before: 60, after: 55, delta: -5 },
              ],
              reasons: [
                { good: false, text: 'Re-entered NIFTY-CE 8 min after SL hit — flagged as revenge trade.' },
                { good: false, text: '6 trades in 90 min — overtrading detected after losing streak.' },
              ],
            },
          ],
          recent_trades: [
            { symbol: 'BANKNIFTY-FUT',  reason: 'Breakout',   confidence: 8, pnl:  3100, journal: true  },
            { symbol: 'NIFTY-23500-PE', reason: 'Strategy',   confidence: 7, pnl:  1900, journal: true  },
            { symbol: 'INFY',           reason: 'Impulse',    confidence: 5, pnl: -2200, journal: false },
            { symbol: 'BANKNIFTY-CE',   reason: 'Tip',        confidence: 4, pnl: -1800, journal: false },
          ],
        },
      },
      {
        // Slide 7 — Score Breakdown bar chart + Component Details + Score Trend sparkline
        title: 'Component breakdown, weights, and your score trend',
        body: 'The page ends with the full score breakdown — a bar chart of all 9 components — then expandable component details showing each weight and what moves it, and finally a score trend line over 7 days. Tap any component to see its weight and the specific behaviour that drives it.',
        icon: 'CheckCircle',
        ui: {
          type: 'discipline-components',
          trend: [
            { label: 'Mon', score: 64 },
            { label: 'Tue', score: 68 },
            { label: 'Wed', score: 65 },
            { label: 'Thu', score: 71 },
            { label: 'Fri', score: 69 },
            { label: 'Sat', score: 73 },
            { label: 'Sun', score: 76 },
          ],
          components: [
            { key: 'stop_loss',    score: 88, note: 'SL set and not moved on 18 of 20 trades. Your strongest component — 2 exceptions cost 7 points. Maintain this.' },
            { key: 'risk_reward',  score: 74, note: 'Target set before entry on 14 of 20 trades. R:R avg 1.8× — above the 1.5 threshold. 6 trades had no defined target.' },
            { key: 'strategy',     score: 76, note: '15 of 20 entries matched a tagged setup. 5 impulse entries pulled this below 80. Eliminate tip-based trades to cross 85.' },
            { key: 'emotion',      score: 58, note: '3 revenge trades flagged — re-entry within 10 min of SL hit. Your biggest drag. One patient session moves this to 72.' },
            { key: 'frequency',    score: 55, note: 'Two sessions exceeded 6 trades. One had 9 in 2 hrs. Overtrading after losses is the pattern — hard-cap at 5 and stop.' },
            { key: 'confidence',   score: 68, note: 'Avg tag 7.2/10 vs 54% win rate — slight overconfidence. Lower tags on B-grade setups to recalibrate.' },
            { key: 'journal',      score: 72, note: 'Note added on 14 of 20 trades. 6 missing — all on losing days. You avoid journaling when it hurts. That\'s exactly when it matters.' },
            { key: 'sl_violation', score: 80, note: 'SL moved outward on 2 trades (widened avg 1.2%). "Giving it room" is the #1 account-killer habit.' },
            { key: 'risk_profile', score: 72, note: 'Position within limit on 18 of 20 trades. 2 trades were 1.4× the profile max. Oversizing on conviction is still oversizing.' },
          ],
        },
        cta: { label: 'Go to Discipline →', path: '/discipline' },
      },
    ],
  },

  {
    id: 'ai-coach',
    label: 'AI Coach\n(Nyx)',
    color: '#8b5cf6',
    zone: 'discipline',
    position: { lat:  42, lon:  158 },
    steps: [
      {
        // Slide 1 — Nyx greeting + the 4 exact real COACH_PROMPTS from AIPage
        title: 'Meet Nyx — your AI trading coach',
        body: 'Tap Discover → AI Coach. Nyx is powered by Claude and connected to your actual TradeReflex account — discipline score, trade history, win rate, and behaviour patterns. It gives answers from your real data, not generic content. Tap each prompt chip to see exactly what Nyx does with it.',
        icon: 'Brain',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'nyx', text: 'Hello, I\'m Nyx, your AI Trading Coach. I have access to your full trading profile — capital, discipline score, trade history, and behavioral patterns. What would you like to work on?' },
          ],
          prompts: ['Review my portfolio', 'Analyze my recent trades', 'Why am I losing money?', 'How to improve win rate?'],
        },
      },
      {
        // Slide 2 — Discipline & score coaching (real use case)
        title: 'Ask about your discipline and score',
        body: 'The most common use: ask Nyx why your discipline score changed, what behaviour is costing you the most, or what pattern is driving your losses. Nyx reads your actual score history and trade log — every answer references your specific numbers, not generic advice.',
        icon: 'Activity',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'user', text: 'Why is my discipline score dropping?' },
            { role: 'nyx',  text: 'Your score dropped from 74 → 62 over the last 10 trades. Main driver: 4 SL violations — all on intraday positions held past 3 PM. Your score recovers fast when you close before 3 PM. Try a hard rule: close all intraday by 3 PM.' },
          ],
          prompts: ['Show my best setup', 'What am I doing wrong?', 'Review my breakout trades', 'Am I overtrading?'],
        },
      },
      {
        // Slide 3 — Post-session pattern analysis (real use case)
        title: 'Post-session pattern analysis',
        body: 'After a bad session or losing streak, bring it to Nyx. Describe what happened and ask what the pattern is. Nyx cross-references your trade history — hold times, exit points, time-of-day bias, SL adherence — and gives one specific fix, not a list of generic tips.',
        icon: 'BarChart2',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'user', text: 'Lost ₹8,000 in last 3 trades — all intraday, all cut winners early. What\'s happening?' },
            { role: 'nyx',  text: 'Pattern: you\'re exiting winners at 30–40% of target in 78% of cases. This is profit anxiety — common after a drawdown. Your avg hold on losers is 2.4× longer than on winners. Fix: don\'t manually exit until price reaches at least 50% of target.' },
          ],
          prompts: ['Analyze my last 3 trades', 'Why do I exit early?', 'Am I revenge trading?', 'Show my timing data'],
        },
      },
      {
        // Slide 4 — Proactive nudges + Market Context toggle + quota ring (all real features)
        title: 'Proactive nudges, Market Context, and quota',
        body: 'Nyx also sends proactive nudges mid-session without you asking — it detects overtrading, revenge patterns, and time-of-day drift from your trade log. The Market Context toggle adds 90-day NSE regime data to answers. The ring in the input bar shows your weekly message quota.',
        icon: 'Brain',
        ui: {
          type: 'chat-preview',
          messages: [
            { role: 'nyx', text: '⚡ Nudge: You\'ve placed 4 trades in the last 90 minutes — all after a ₹2,200 loss. Your win rate drops to 18% in sessions like this. Consider a 30-minute break before your next trade.' },
            { role: 'nyx', text: '📊 Insight: Your last 6 winning trades were all between 9:15–10:30 AM. After 12 PM your win rate is 31%. Shift your active trading window to the opening hour.' },
          ],
          prompts: ['Got it', 'Why this pattern?', 'Show my timing data'],
          show_market_ctx: true,
        },
        cta: { label: 'Go to AI Coach →', path: '/ai-coach' },
      },
    ],
  },

  {
    id: 'risk-profile',
    label: 'Risk\nProfile',
    color: '#8b5cf6',
    zone: 'discipline',
    position: { lat:  -5, lon:  132 },
    steps: [
      {
        // Slide 1 — Main score card + 5-band spectrum (mirrors RiskProfileSection score card exactly)
        title: 'Your Risk Score — what it means',
        body: 'Go to Profile → Risk Profile. The score (0–100) places you on a spectrum from Very Conservative to Very Aggressive — this determines your recommended risk per trade, max trades per day, and trading style. Answer the 13-question assessment honestly for an accurate score.',
        icon: 'Shield',
        ui: {
          type: 'risk-score-card',
          score: 46,
          profile_type: 'Moderate',
        },
      },
      {
        // Slide 2 — Score Breakdown: 3 bars (Capacity 30, Knowledge 20, Behaviour 50)
        title: 'The three components behind your score',
        body: 'Your risk score is built from three sub-scores: Financial Capacity (max 30 pts), Knowledge (max 20 pts), and Behaviour (max 50 pts — the heaviest). The lowest component caps your overall profile. Tap each bar to understand what drives it.',
        icon: 'BarChart2',
        ui: {
          type: 'risk-breakdown',
          capacity: 18,
          knowledge: 12,
          behavior:  24,
        },
      },
      {
        // Slide 3 — Recommendations 3-column grid (Risk%, Style, Max Trades)
        title: 'Your personalised recommendations',
        body: 'Based on your score, the page sets three calibrated recommendations: Risk % per trade, recommended trading Style, and Max trades per day. These aren\'t hard limits — they\'re matched to your actual financial capacity and behavioural history.',
        icon: 'Target',
        ui: {
          type: 'risk-recommendations',
          risk_pct: '1.5%',
          style: 'Swing',
          max_trades: 3,
          adherence_score: 83,
          trades_checked: 42,
          within_limit: 38,
          violations: 4,
          breaches: [
            { symbol: 'NIFTY-23450-CE', date: '2026-05-30', actual_pct: '2.89', limit_pct: '2', severity: 'moderate' },
            { symbol: 'NIFTY-23350-PE', date: '2026-06-01', actual_pct: '2.25', limit_pct: '2', severity: 'moderate' },
          ],
        },
      },
      {
        // Slide 4 — The 13-question assessment flow (one question per screen)
        title: 'The 13-question assessment',
        body: 'Tap "Edit Answers" to update your profile. You answer 13 questions across three categories — Financial Capacity, Knowledge & Experience, and Behavioral Traits. Each question is shown one at a time. Tap any option below to see what it signals about your risk capacity.',
        icon: 'FileText',
        ui: {
          type: 'risk-questionnaire',
          category: 'Behavioral Traits',
          step: '3 of 6',
          question: 'What do you do if a position moves 20% against you?',
          options: [
            { key: 'a', label: 'Exit immediately — no second-guessing',          note: 'Signals strong emotional discipline under loss. This is the highest-scoring response for Behavioral Traits. Traders who exit cleanly have fewer revenge trades and better long-term records.' },
            { key: 'b', label: 'Wait a bit and see if it recovers',               note: 'This is hope-based holding — common but costly. Markets don\'t "owe" a recovery. This lowers your Behaviour score as it suggests you move mental SLs when under pressure.' },
            { key: 'c', label: 'Average down to lower my break-even',             note: 'Averaging into a losing position multiplies risk on a thesis that\'s already failing. Lowest-scoring response — signals high emotional bias and risk of catastrophic loss.' },
            { key: 'd', label: 'Check my original thesis before deciding',        note: 'Structured decision-making. If the thesis is intact, hold with SL. If thesis broken, exit. Mid-high score — better than hope-holding, not as clean as a pre-set SL exit.' },
          ],
        },
      },
      {
        // Slide 5 — Growth over time — profile upgrade path
        title: 'Your profile grows as you improve',
        body: 'Revisit and re-answer the questionnaire every 3 months. As your Knowledge score improves (completing Learning Path levels) and your Behaviour score improves (trading history), your profile upgrades — unlocking higher risk % and more trades per day.',
        icon: 'CheckCircle',
        ui: {
          type: 'risk-breakdown',
          capacity: 24,
          knowledge: 17,
          behavior:  38,
        },
        cta: { label: 'Go to Risk Profile →', path: '/risk-profile' },
      },
    ],
  },

  {
    id: 'risk-calculator',
    label: 'Risk\nCalculator',
    color: '#8b5cf6',
    zone: 'discipline',
    position: { lat: -38, lon:  148 },
    steps: [
      {
        // Slide 1 — What it solves + Capital Summary tiles
        title: 'What the Calculator solves',
        body: 'Tap Discover → Calculator. The most common mistake is sizing on gut feel — "let me buy 100 shares" with no connection to how much you\'re willing to lose. The Calculator tells you exactly how many shares or lots to take based on your entry, SL, and risk budget. Check the 3 tiles at the top first — they set your limits for the session.',
        icon: 'Calculator',
        ui: {
          type: 'limits-panel',
          limits: [
            { label: 'Daily Limit',       value: '₹1,50,000', color: '#6366f1', detail: 'Your daily capital cap — set by your Discipline Score tier. At 72/100 you\'re in Advanced tier (₹1.5L/day). Improve above 80 to unlock Elite tier (₹2L/day).' },
            { label: 'Risk / Trade',      value: '2%',         color: '#16C784', detail: 'From your Risk Profile (Aggressive). 2% of ₹1.5L = ₹3,000 max loss per trade. This is auto-filled in the Plan Setup below — don\'t override without reason.' },
            { label: 'Max Trades',        value: '5',          color: '#f59e0b', detail: 'Maximum trades allowed today from your Risk Profile. Plan all 5 slots upfront — don\'t place trades one at a time and discover you\'ve overused capital.' },
          ],
        },
      },
      {
        // Slide 2 — Plan Setup (account size, trades stepper, risk %, allocation bars)
        title: 'Plan Setup — build your session plan',
        body: 'Enter your account size and number of trades today. The calculator splits capital across slots and shows the allocation bar for each trade. You can adjust individual allocations with −/+ buttons. The 3 chips show: total risk budget, total risk %, and capital per trade slot.',
        icon: 'Wallet',
        ui: {
          type: 'calculator-plan',
          capital:    150000,
          num_trades: 5,
          risk_pct:   2,
          allocs:     [20, 20, 20, 20, 20],
        },
      },
      {
        // Slide 3 — Equity trade slot (stock, entry, SL/target steppers, auto quantity, breakdown)
        title: 'Equity trade slot — auto position sizing',
        body: 'Expand a trade slot and set it to Equity. Enter the stock, entry price, and SL %. Quantity is calculated automatically — no manual maths. The slot shows max loss, max profit, and R:R. If R:R is below 1.5 the slot shows a warning. Never place a trade where you don\'t know your max loss before entering.',
        icon: 'Hash',
        ui: {
          type: 'calculator-equity-slot',
          symbol:      'RELIANCE',
          entry:       2840,
          sl_pct:      1.5,
          target_pct:  3.2,
          capital:     30000,
          risk_budget: 3000,
        },
      },
      {
        // Slide 4 — F&O trade slot (underlying, options/futures, direction, lots, strike, premium/SL/target)
        title: 'F&O trade slot — options and futures',
        body: 'Switch a slot to F&O. Select the underlying (NIFTY/BANKNIFTY), instrument type (Options or Futures), direction, and for options: CE or PE. Set lots, pick a strike and expiry. Enter premium, SL premium, and target premium. Margin is calculated automatically and max loss is shown before you place.',
        icon: 'Layers',
        ui: {
          type: 'calculator-fo-slot',
          underlying:      'NIFTY',
          instrument:      'Options',
          direction:       'BUY',
          option_type:     'CE',
          lots:            1,
          lot_size:        75,
          strike:          '24000',
          expiry:          '29 May',
          premium:         180,
          sl_premium:      120,
          target_premium:  280,
          margin:          13500,
        },
      },
      {
        // Slide 5 — Plan Summary (best/worst case split, R:R bar, stat list)
        title: 'Plan Summary — best case and worst case',
        body: 'Once you\'ve configured your slots, the Plan Summary shows the full picture: best case P&L (all targets hit), worst case P&L (all SLs hit), the plan R:R, total capital deployed, brokerage, and net P&L. Look at the worst case first. If you can absorb that loss without changing your behaviour — proceed.',
        icon: 'CheckCircle',
        ui: {
          type: 'calculator-summary',
          num_trades:       5,
          num_filled:       5,
          best_case:        7200,
          worst_case:       3000,
          capital:          150000,
          total_deployed:   90000,
          total_max_loss:   3000,
          total_max_profit: 7500,
          brokerage:        420,
          rr:               2.4,
        },
        cta: { label: 'Go to Calculator →', path: '/calculator' },
      },
    ],
  },

];
